import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LayoutDashboard, Package, ShoppingCart, Users, Settings, LogOut, Menu, X, Eye, Trash2, Edit2, ToggleLeft, ToggleRight, ChevronDown, Home, BarChart3, Tag, Mail, Image, Video, Upload, Plus, ArrowUp, ArrowDown, GripVertical, FileText, Download, BookOpen, CalendarCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── ADMIN LOGIN ──────────────────────────────────────────────────
function AdminLogin({ onLogin }) {
    const [form, setForm] = useState({ email: '', password: '' });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault(); setError(''); setLoading(true);
        try {
            const r = await axios.post(`${API}/auth/login`, form, { withCredentials: true });
            if (r.data.role !== 'admin') { setError('This account does not have admin access.'); setLoading(false); return; }
            onLogin(r.data);
        } catch(err) {
            setError(err.response?.data?.detail || 'Login failed');
        } finally { setLoading(false); }
    };

    return (
        <div className="min-h-screen bg-navy flex items-center justify-center p-4">
            <div className="bg-ivory rounded shadow-lg max-w-md w-full p-10">
                <div className="text-center mb-8">
                    <h1 className="font-heading text-3xl text-navy font-bold">KNOW YOUR STUFF</h1>
                    <p className="font-body text-navy/60 mt-2">Admin Panel</p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="admin-login-form">
                    <div>
                        <label className="font-body text-navy/70 text-sm font-semibold block mb-1">Email</label>
                        <input type="email" required value={form.email} onChange={e=>setForm({...form,email:e.target.value})} className="w-full bg-white text-navy border border-gray-300 rounded-sm px-4 py-3 font-body text-base min-h-[44px] focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent" data-testid="admin-email-input"/>
                    </div>
                    <div>
                        <label className="font-body text-navy/70 text-sm font-semibold block mb-1">Password</label>
                        <input type="password" required value={form.password} onChange={e=>setForm({...form,password:e.target.value})} className="w-full bg-white text-navy border border-gray-300 rounded-sm px-4 py-3 font-body text-base min-h-[44px] focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent" data-testid="admin-password-input"/>
                    </div>
                    {error && <p className="text-red-600 text-sm font-body bg-red-50 border border-red-200 rounded p-3" data-testid="admin-login-error">{error}</p>}
                    <button type="submit" disabled={loading} className="w-full bg-gold hover:bg-gold-hover disabled:opacity-50 text-navy font-bold py-3 rounded-sm font-body text-lg transition-colors min-h-[44px]" data-testid="admin-login-submit-btn">
                        {loading ? 'Signing in...' : 'Sign In'}
                    </button>
                </form>
            </div>
        </div>
    );
}

// ─── DASHBOARD ────────────────────────────────────────────────────
function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [visitorCount, setVisitorCount] = useState(null);
    useEffect(() => {
        axios.get(`${API}/admin/dashboard`, { withCredentials: true }).then(r=>setStats(r.data)).catch(()=>{});
        axios.get(`${API}/visits/count`).then(r=>setVisitorCount(r.data.total)).catch(()=>{});
    }, []);
    const cards = stats ? [
        { label: 'Unique Visitors', value: visitorCount === null ? '—' : visitorCount.toLocaleString(), color: 'bg-gold/10 border-gold/30', testId: 'unique-visitors' },
        { label: 'Total Products', value: stats.total_products, color: 'bg-blue-50 border-blue-200' },
        { label: 'Total Orders', value: stats.total_orders, color: 'bg-green-50 border-green-200' },
        { label: 'Customers', value: stats.total_customers, color: 'bg-purple-50 border-purple-200' },
        { label: 'Total Revenue', value: `$${parseFloat(stats.total_revenue||0).toFixed(2)}`, color: 'bg-gold/10 border-gold/30' },
    ] : [];
    const alerts = stats?.alerts || [];
    const alertColors = { warning: 'bg-yellow-50 border-yellow-300 text-yellow-900', danger: 'bg-red-50 border-red-300 text-red-900', info: 'bg-blue-50 border-blue-300 text-blue-900' };
    return (
        <div data-testid="admin-dashboard">
            <h2 className="font-heading text-2xl text-navy font-bold mb-8">Dashboard</h2>
            {alerts.length > 0 && (
                <div className="space-y-3 mb-8" data-testid="security-alerts">
                    {alerts.map((a,i) => (
                        <div key={i} className={`rounded-lg border p-4 font-body text-sm ${alertColors[a.type] || alertColors.info}`} data-testid={`alert-${i}`}>
                            <strong className="uppercase text-xs tracking-wider mr-2">{a.type === 'danger' ? 'Alert' : a.type === 'warning' ? 'Warning' : 'Info'}:</strong> {a.message}
                        </div>
                    ))}
                </div>
            )}
            {!stats ? <p className="font-body text-navy/60">Loading stats...</p> : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                    {cards.map(c=>(
                        <div key={c.label} className={`rounded border p-6 ${c.color}`} data-testid={`stat-${c.testId || c.label.toLowerCase().replace(/\s/g,'-')}`}>
                            <p className="font-body text-navy/60 text-sm uppercase tracking-wider mb-2">{c.label}</p>
                            <p className="font-heading text-3xl text-navy font-bold">{c.value}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── PRODUCTS ─────────────────────────────────────────────────────
function AdminProducts() {
    const [products, setProducts] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editProduct, setEditProduct] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [formData, setFormData] = useState({ title:'', type:'physical', price:'', description:'', download_limit:'5', download_expiry_days:'30', is_active:'true', unlimited_downloads: false, no_expiry: false, preview_start:'00:00', preview_end:'00:45' });
    const [imageFile, setImageFile] = useState(null);
    const [additionalImageFiles, setAdditionalImageFiles] = useState(null);
    const [ebookFile, setEbookFile] = useState(null);
    const [readerFile, setReaderFile] = useState(null);
    const [audioFile, setAudioFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState('');
    const [err, setErr] = useState('');

    const load = () => axios.get(`${API}/admin/products`, { withCredentials: true }).then(r=>setProducts(r.data)).catch(()=>{});
    useEffect(() => { load(); }, []);

    const openAdd = () => { setEditProduct(null); setFormData({ title:'', type:'physical', price:'', description:'', download_limit:'5', download_expiry_days:'30', is_active:'true', unlimited_downloads: false, no_expiry: false, preview_start:'00:00', preview_end:'00:45' }); setImageFile(null); setAdditionalImageFiles(null); setEbookFile(null); setReaderFile(null); setAudioFile(null); setErr(''); setShowForm(true); };
    const openEdit = (p) => {
        const unlimitedDl = p.download_limit === -1;
        const noExp = p.download_expiry_days === -1;
        setEditProduct(p);
        setFormData({
            title:p.title, type:p.type, price:String(p.price), description:p.description||'',
            download_limit: unlimitedDl ? '5' : String(p.download_limit||5),
            download_expiry_days: noExp ? '30' : String(p.download_expiry_days||30),
            is_active:String(p.is_active),
            unlimited_downloads: unlimitedDl,
            no_expiry: noExp,
            preview_start: p.preview_start || '00:00',
            preview_end: p.preview_end || '00:45'
        });
        setImageFile(null); setAdditionalImageFiles(null); setEbookFile(null); setReaderFile(null); setAudioFile(null); setErr(''); setShowForm(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault(); setErr(''); setLoading(true);
        const fd = new FormData();
        // Build form data, sending -1 for unlimited
        const submitData = { ...formData };
        if (formData.unlimited_downloads) submitData.download_limit = '-1';
        if (formData.no_expiry) submitData.download_expiry_days = '-1';
        Object.entries(submitData).forEach(([k,v]) => {
            if (k === 'unlimited_downloads' || k === 'no_expiry') return;
            fd.append(k, v);
        });
        if (imageFile) fd.append('image', imageFile);
        else if (!editProduct) { setErr('Product image is required'); setLoading(false); return; }
        if (formData.type === 'ebook' && ebookFile) fd.append('ebook_file', ebookFile);
        else if (formData.type === 'ebook' && !editProduct && !ebookFile) { setErr('E-book file is required'); setLoading(false); return; }
        if (formData.type === 'ebook' && readerFile) fd.append('reader_file', readerFile);
        if (formData.type === 'audiobook' && audioFile) fd.append('audio_file', audioFile);
        else if (formData.type === 'audiobook' && !editProduct && !audioFile) { setErr('Audio file is required for audiobooks.'); setLoading(false); return; }
        // /^\d{1,3}:\d{2}$/ time validation for audiobook preview timestamps
        if (formData.type === 'audiobook') {
            const re = /^\d{1,3}:\d{2}$/;
            if (!re.test(formData.preview_start || '') || !re.test(formData.preview_end || '')) {
                setErr('Preview timestamps must be in M:SS or MM:SS format.'); setLoading(false); return;
            }
        }

        try {
            if (editProduct) {
                await axios.put(`${API}/admin/products/${editProduct.product_id}`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }});
                // Upload additional images if provided
                if (additionalImageFiles && additionalImageFiles.length > 0) {
                    const imgFd = new FormData();
                    for (const f of additionalImageFiles) imgFd.append('images', f);
                    await axios.post(`${API}/admin/products/${editProduct.product_id}/images`, imgFd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }});
                }
                setMsg('Product updated successfully.');
            } else {
                const r = await axios.post(`${API}/admin/products`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }});
                // Upload additional images if provided
                if (additionalImageFiles && additionalImageFiles.length > 0) {
                    const imgFd = new FormData();
                    for (const f of additionalImageFiles) imgFd.append('images', f);
                    await axios.post(`${API}/admin/products/${r.data.product_id}/images`, imgFd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }});
                }
                setMsg('Product added successfully.');
            }
            setShowForm(false); load();
        } catch(ex) { setErr(ex.response?.data?.detail || 'Failed to save product'); }
        finally { setLoading(false); }
    };

    const handleDelete = async (productId) => {
        try {
            await axios.delete(`${API}/admin/products/${productId}`, { withCredentials: true });
            setMsg('Product deleted.'); setDeleteConfirm(null); load();
        } catch { setErr('Failed to delete product'); }
    };

    const handleToggle = async (product) => {
        try {
            await axios.patch(`${API}/admin/products/${product.product_id}/toggle`, {}, { withCredentials: true });
            load();
        } catch {}
    };

    const handleDeleteAdditionalImage = async (productId, index) => {
        try {
            await axios.delete(`${API}/admin/products/${productId}/images/${index}`, { withCredentials: true });
            if (editProduct) {
                const updated = { ...editProduct };
                updated.additional_images = (updated.additional_images || []).filter((_, i) => i !== index);
                setEditProduct(updated);
            }
            load();
        } catch {}
    };

    const inputCls = "w-full bg-white text-navy border border-gray-300 rounded-sm px-4 py-3 font-body text-base min-h-[44px] focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent";
    const labelCls = "font-body text-navy/70 text-sm font-semibold block mb-1";

    return (
        <div data-testid="admin-products">
            <div className="flex items-center justify-between mb-8">
                <h2 className="font-heading text-2xl text-navy font-bold">Products</h2>
                <button onClick={openAdd} className="bg-gold hover:bg-gold-hover text-navy font-bold px-6 py-2 rounded-sm font-body transition-colors min-h-[44px]" data-testid="add-product-btn">+ Add Product</button>
            </div>
            {msg && <p className="text-green-700 bg-green-50 border border-green-200 rounded p-3 font-body text-sm mb-4">{msg}</p>}
            {err && !showForm && <p className="text-red-600 bg-red-50 border border-red-200 rounded p-3 font-body text-sm mb-4">{err}</p>}

            {/* Product Table */}
            <div className="overflow-x-auto bg-white rounded border border-gray-200">
                <table className="w-full font-body text-sm" data-testid="products-table">
                    <thead><tr className="border-b border-gray-200 text-navy/60 text-left bg-ivory">
                        <th className="py-3 px-4">Image</th><th className="py-3 px-4">Title</th><th className="py-3 px-4">Type</th><th className="py-3 px-4">Price</th><th className="py-3 px-4">Photos</th><th className="py-3 px-4">Status</th><th className="py-3 px-4">Actions</th>
                    </tr></thead>
                    <tbody>{products.map(p=>(
                        <tr key={p.product_id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4">
                                {p.image_path ? <img src={`${API}/files/${p.image_path}`} alt={p.title} className="w-12 h-12 object-cover rounded"/> : <div className="w-12 h-12 bg-navy/10 rounded"/>}
                            </td>
                            <td className="py-3 px-4 font-semibold text-navy">{p.title}</td>
                            <td className="py-3 px-4"><span className={`text-xs px-2 py-1 rounded ${p.type==='ebook'?'bg-gold/20 text-navy':p.type==='audiobook'?'bg-navy/10 text-navy':'bg-gray-100 text-navy/70'}`} data-testid={`product-type-badge-${p.product_id}`}>{p.type==='ebook'?'E-Book':p.type==='audiobook'?'Audiobook':'Physical'}</span></td>
                            <td className="py-3 px-4 text-navy">${parseFloat(p.price).toFixed(2)}</td>
                            <td className="py-3 px-4 text-navy/60 text-xs">
                                1 main{(p.additional_images && p.additional_images.length > 0) ? ` + ${p.additional_images.length} extra` : ''}
                            </td>
                            <td className="py-3 px-4">
                                <button onClick={()=>handleToggle(p)} className={`flex items-center gap-1 text-xs font-bold ${p.is_active?'text-green-600':'text-gray-400'}`} data-testid={`toggle-product-${p.product_id}`}>
                                    {p.is_active ? <ToggleRight size={20}/> : <ToggleLeft size={20}/>} {p.is_active?'Active':'Inactive'}
                                </button>
                            </td>
                            <td className="py-3 px-4">
                                <div className="flex items-center gap-2">
                                    <button onClick={()=>openEdit(p)} className="text-navy/60 hover:text-gold transition-colors p-1" title="Edit" data-testid={`edit-product-${p.product_id}`}><Edit2 size={16}/></button>
                                    {p.type === 'ebook' && p.file_path && (
                                        <a href={`${API}/admin/products/${p.product_id}/download-file?file_type=pdf`} className="text-navy/60 hover:text-green-600 transition-colors p-1" title="Download PDF" data-testid={`admin-dl-pdf-${p.product_id}`}><Download size={16}/></a>
                                    )}
                                    {p.type === 'ebook' && p.reader_file_path && (
                                        <a href={`${API}/admin/products/${p.product_id}/download-file?file_type=html`} className="text-navy/60 hover:text-blue-600 transition-colors p-1" title="Download Reader HTML" data-testid={`admin-dl-html-${p.product_id}`}><BookOpen size={16}/></a>
                                    )}
                                    <button onClick={()=>setDeleteConfirm(p)} className="text-navy/60 hover:text-red-600 transition-colors p-1" title="Delete" data-testid={`delete-product-${p.product_id}`}><Trash2 size={16}/></button>
                                </div>
                            </td>
                        </tr>
                    ))}</tbody>
                </table>
                {products.length === 0 && <p className="font-body text-navy/60 text-center py-8" data-testid="no-products-admin-msg">No products yet. Add your first product above.</p>}
            </div>

            {/* Delete Confirm */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-navy/80 backdrop-blur flex items-center justify-center z-50 p-4" data-testid="delete-confirm-modal">
                    <div className="bg-white rounded p-8 max-w-sm w-full">
                        <h3 className="font-heading text-xl text-navy font-bold mb-4">Delete Product</h3>
                        <p className="font-body text-navy/70 mb-6">Are you sure you want to delete <strong>{deleteConfirm.title}</strong>? This cannot be undone.</p>
                        <div className="flex gap-4">
                            <button onClick={()=>handleDelete(deleteConfirm.product_id)} className="bg-red-600 text-white font-bold px-6 py-2 rounded font-body min-h-[44px]" data-testid="confirm-delete-btn">Delete</button>
                            <button onClick={()=>setDeleteConfirm(null)} className="bg-gray-100 text-navy font-bold px-6 py-2 rounded font-body min-h-[44px]" data-testid="cancel-delete-btn">Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add/Edit Form */}
            {showForm && (
                <div className="fixed inset-0 bg-navy/80 backdrop-blur z-50 flex items-start justify-center overflow-y-auto" data-testid="product-form-modal">
                    <div className="bg-white rounded w-full max-w-lg mx-4 my-4">
                        <div className="sticky top-0 bg-white rounded-t px-6 py-4 border-b border-gray-200 flex justify-between items-center z-10">
                            <h3 className="font-heading text-xl text-navy font-bold">{editProduct ? 'Edit Product' : 'Add New Product'}</h3>
                            <button onClick={()=>setShowForm(false)} className="text-navy/40 hover:text-navy"><X size={20}/></button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            <div><label className={labelCls}>Product Title *</label><input type="text" required value={formData.title} onChange={e=>setFormData({...formData,title:e.target.value})} className={inputCls} data-testid="product-title-input"/></div>
                            <div><label className={labelCls}>Product Type *</label>
                                <select value={formData.type} onChange={e=>setFormData({...formData,type:e.target.value})} className={inputCls} data-testid="product-type-select">
                                    <option value="physical">Physical Book</option>
                                    <option value="ebook">E-Book / Digital Download</option>
                                    <option value="audiobook">Audiobook</option>
                                </select>
                            </div>
                            <div><label className={labelCls}>Price (USD) *</label><input type="number" required min="0.01" step="0.01" value={formData.price} onChange={e=>setFormData({...formData,price:e.target.value})} className={inputCls} data-testid="product-price-input"/></div>
                            <div><label className={labelCls}>Description</label><textarea value={formData.description} onChange={e=>setFormData({...formData,description:e.target.value})} rows={3} maxLength={500} className={inputCls} data-testid="product-description-input"/></div>
                            <div>
                                <label className={labelCls}>Product Image {!editProduct && '*'}</label>
                                <div className="flex items-center gap-3">
                                    {editProduct && editProduct.image_path && (
                                        <img src={`${API}/files/${editProduct.image_path}`} alt="Current" className="w-14 h-14 object-cover rounded border border-gold/30 flex-shrink-0" data-testid="current-main-image-preview"/>
                                    )}
                                    <div className="flex-1">
                                        <input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={e=>setImageFile(e.target.files[0])} className="w-full font-body text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-gold file:text-navy file:font-bold file:text-sm cursor-pointer" data-testid="product-image-input"/>
                                        {editProduct && <p className="text-[11px] text-navy/40 mt-0.5">Leave empty to keep current</p>}
                                    </div>
                                </div>
                            </div>
                            {/* Additional Images — compact */}
                            <div>
                                <label className={labelCls}>Additional Photos</label>
                                {editProduct && editProduct.additional_images && editProduct.additional_images.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mb-2" data-testid="current-additional-images-preview">
                                        {editProduct.additional_images.map((imgPath, idx) => (
                                            <div key={idx} className="relative">
                                                <img src={`${API}/files/${imgPath}`} alt={`Extra ${idx+1}`} className="w-12 h-12 object-cover rounded border border-gray-200"/>
                                                <button type="button" onClick={()=>handleDeleteAdditionalImage(editProduct.product_id, idx)} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center hover:bg-red-600" data-testid={`delete-additional-image-${idx}`}>
                                                    <X size={10}/>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <input type="file" accept=".jpg,.jpeg,.png,.webp" multiple onChange={e=>setAdditionalImageFiles(e.target.files ? Array.from(e.target.files) : null)} className="w-full font-body text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-gold/70 file:text-navy file:font-bold file:text-sm cursor-pointer" data-testid="product-additional-images-input"/>
                            </div>
                            {formData.type === 'ebook' && (
                                <>
                                    <div>
                                        <label className={labelCls}>E-Book PDF (for download and email delivery) {!editProduct && '*'}</label>
                                        <input type="file" accept=".pdf,.epub,application/pdf,application/epub+zip,application/octet-stream" onChange={e=>setEbookFile(e.target.files[0])} className="w-full font-body text-base file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-navy file:text-white file:font-bold cursor-pointer" data-testid="product-ebook-input"/>
                                        {editProduct && editProduct.file_path && <p className="text-xs text-green-600 mt-1">Current PDF uploaded. Upload new file to replace.</p>}
                                        {editProduct && !editProduct.file_path && <p className="text-xs text-orange-600 mt-1">No PDF uploaded yet.</p>}
                                    </div>
                                    <div>
                                        <label className={labelCls}>E-Book Reader File (HTML — for in-platform reading)</label>
                                        <input type="file" accept=".html,.htm,text/html" onChange={e=>setReaderFile(e.target.files[0])} className="w-full font-body text-base file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-navy file:text-white file:font-bold cursor-pointer" data-testid="product-reader-input"/>
                                        {editProduct && editProduct.reader_file_path && <p className="text-xs text-green-600 mt-1">Reader HTML uploaded. Upload new file to replace.</p>}
                                        {editProduct && !editProduct.reader_file_path && <p className="text-xs text-orange-600 mt-1">No reader file uploaded yet.</p>}
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className={labelCls}>Download Limit</label>
                                            <div className="flex items-center gap-3 mb-2">
                                                <input type="checkbox" checked={formData.unlimited_downloads} onChange={e=>setFormData({...formData, unlimited_downloads: e.target.checked})} className="w-5 h-5 accent-gold" data-testid="unlimited-downloads-checkbox"/>
                                                <span className="font-body text-navy/70 text-sm">Unlimited</span>
                                            </div>
                                            {!formData.unlimited_downloads && (
                                                <input type="number" min="1" value={formData.download_limit} onChange={e=>setFormData({...formData,download_limit:e.target.value})} className={inputCls} data-testid="product-download-limit-input"/>
                                            )}
                                        </div>
                                        <div>
                                            <label className={labelCls}>Expiry (days)</label>
                                            <div className="flex items-center gap-3 mb-2">
                                                <input type="checkbox" checked={formData.no_expiry} onChange={e=>setFormData({...formData, no_expiry: e.target.checked})} className="w-5 h-5 accent-gold" data-testid="no-expiry-checkbox"/>
                                                <span className="font-body text-navy/70 text-sm">No Expiry</span>
                                            </div>
                                            {!formData.no_expiry && (
                                                <input type="number" min="1" value={formData.download_expiry_days} onChange={e=>setFormData({...formData,download_expiry_days:e.target.value})} className={inputCls} data-testid="product-expiry-input"/>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}
                            {formData.type === 'audiobook' && (
                                <>
                                    <div>
                                        <label className={labelCls}>Audio File (uploaded to bunny.net CDN) {!editProduct && '*'}</label>
                                        <input type="file" accept=".mp3,.m4a,.m4b,.wav,audio/mpeg,audio/mp4,audio/wav" onChange={e=>setAudioFile(e.target.files[0])} className="w-full font-body text-base file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-navy file:text-white file:font-bold cursor-pointer" data-testid="product-audio-input"/>
                                        <p className="text-[11px] text-navy/40 mt-1">MP3, M4A/M4B or WAV · up to 500 MB. Streamed from bunny.net.</p>
                                        {editProduct && editProduct.audio_url && <p className="text-xs text-green-600 mt-1" data-testid="audiobook-current-audio-note">Audio uploaded. Upload new file to replace.</p>}
                                        {editProduct && !editProduct.audio_url && <p className="text-xs text-orange-600 mt-1">No audio uploaded yet.</p>}
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className={labelCls}>Preview Start (MM:SS)</label>
                                            <input type="text" pattern="^\d{1,3}:\d{2}$" placeholder="00:00" value={formData.preview_start} onChange={e=>setFormData({...formData,preview_start:e.target.value})} className={inputCls} data-testid="audiobook-preview-start-input"/>
                                        </div>
                                        <div>
                                            <label className={labelCls}>Preview End (MM:SS)</label>
                                            <input type="text" pattern="^\d{1,3}:\d{2}$" placeholder="00:45" value={formData.preview_end} onChange={e=>setFormData({...formData,preview_end:e.target.value})} className={inputCls} data-testid="audiobook-preview-end-input"/>
                                        </div>
                                    </div>
                                    <p className="text-[11px] text-navy/50 -mt-2">Buyers get a 100-download cap plus unlimited in-app streaming. Public shop plays only the preview range above.</p>
                                </>
                            )}
                            <div className="flex items-center gap-3">
                                <label className={labelCls + ' mb-0'}>Active</label>
                                <input type="checkbox" checked={formData.is_active==='true'} onChange={e=>setFormData({...formData,is_active:e.target.checked?'true':'false'})} className="w-5 h-5 accent-gold" data-testid="product-active-checkbox"/>
                            </div>
                            {err && <p className="text-red-600 text-sm font-body bg-red-50 border border-red-200 rounded p-3" data-testid="product-form-error">{err}</p>}
                            <div className="flex gap-4 pt-2">
                                <button type="submit" disabled={loading} className="flex-1 bg-gold hover:bg-gold-hover disabled:opacity-50 text-navy font-bold py-3 rounded-sm font-body transition-colors min-h-[44px]" data-testid="product-form-submit-btn">
                                    {loading ? 'Saving...' : editProduct ? 'Update Product' : 'Add Product'}
                                </button>
                                <button type="button" onClick={()=>setShowForm(false)} className="flex-1 bg-gray-100 text-navy font-bold py-3 rounded-sm font-body min-h-[44px]">Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── ORDERS ───────────────────────────────────────────────────────
function AdminOrders() {
    const [orders, setOrders] = useState([]);
    const [search, setSearch] = useState('');
    const [savedStatus, setSavedStatus] = useState({});
    const [labelOrder, setLabelOrder] = useState(null);  // order currently in label modal

    const load = () => axios.get(`${API}/admin/orders`, { withCredentials: true }).then(r=>setOrders(r.data)).catch(()=>{});
    useEffect(() => { load(); }, []);

    const updateStatus = async (orderId, newStatus) => {
        try {
            await axios.patch(`${API}/admin/orders/${orderId}/status`, { status: newStatus }, { withCredentials: true });
            setSavedStatus(prev=>({...prev, [orderId]: true}));
            setTimeout(()=>setSavedStatus(prev=>({...prev,[orderId]:false})),2000);
            load();
        } catch {}
    };

    const statusLabel = s => ({ pending_fulfillment:'Processing', shipped:'Shipped', delivered:'Delivered', cancelled:'Cancelled', delivered_digital:'Delivered (Digital)' }[s] || s);
    const filtered = orders.filter(o => !search || o.customer_name?.toLowerCase().includes(search.toLowerCase()) || o.customer_email?.toLowerCase().includes(search.toLowerCase()) || o.order_id?.toLowerCase().includes(search.toLowerCase()));
    const totalRevenue = orders.filter(o=>o.status!=='cancelled').reduce((sum,o)=>sum+parseFloat(o.amount||0),0);

    return (
        <div data-testid="admin-orders">
            <h2 className="font-heading text-2xl text-navy font-bold mb-4">Orders</h2>
            <div className="flex flex-wrap gap-4 items-center justify-between mb-6">
                <div className="flex gap-4 font-body text-sm text-navy/60">
                    <span>Total: <strong className="text-navy">{orders.length}</strong></span>
                    <span>Revenue: <strong className="text-gold">${totalRevenue.toFixed(2)}</strong></span>
                </div>
                <input type="text" placeholder="Search by name, email or order ID..." value={search} onChange={e=>setSearch(e.target.value)} className="bg-white text-navy border border-gray-300 rounded-sm px-4 py-2 font-body text-base min-h-[44px] focus:outline-none focus:ring-2 focus:ring-gold w-64" data-testid="orders-search-input"/>
            </div>
            <div className="overflow-x-auto bg-white rounded border border-gray-200">
                <table className="w-full font-body text-sm" data-testid="orders-table">
                    <thead><tr className="border-b border-gray-200 text-navy/60 text-left bg-ivory">
                        <th className="py-3 px-4">Order</th><th className="py-3 px-4">Customer</th><th className="py-3 px-4">Shipping</th><th className="py-3 px-4">Product</th><th className="py-3 px-4">Amount</th><th className="py-3 px-4">Date</th><th className="py-3 px-4">Status</th><th className="py-3 px-4">Label</th>
                    </tr></thead>
                    <tbody>{filtered.map(o=>(
                        <tr key={o.order_id} className="border-b border-gray-100">
                            <td className="py-3 px-4 text-navy/60 font-mono text-xs">{o.order_id?.slice(0,8)}...</td>
                            <td className="py-3 px-4">
                                <div className="font-semibold text-navy">{o.customer_name}</div>
                                <div className="text-navy/60 text-xs">{o.customer_email}</div>
                                {o.buyer_contact?.email && o.buyer_contact.email.toLowerCase() !== (o.customer_email || '').toLowerCase() && (
                                    <div className="text-navy/60 text-xs" data-testid={`order-paypal-email-${o.order_id}`}>PayPal: {o.buyer_contact.email}</div>
                                )}
                                {o.buyer_contact?.phone && <div className="text-navy/60 text-xs">{o.buyer_contact.phone}</div>}
                            </td>
                            <td className="py-3 px-4 text-xs text-navy/70 align-top" data-testid={`order-shipping-${o.order_id}`}>
                                {o.shipping_address ? (
                                    <div>
                                        <div className="font-semibold text-navy">{o.shipping_address.recipient_name || `${o.buyer_contact?.first_name || ''} ${o.buyer_contact?.last_name || ''}`.trim()}</div>
                                        <div>{o.shipping_address.line1}</div>
                                        {o.shipping_address.line2 && <div>{o.shipping_address.line2}</div>}
                                        <div>{[o.shipping_address.city, o.shipping_address.state, o.shipping_address.postal_code].filter(Boolean).join(', ')}</div>
                                        {o.shipping_address.country_code && <div>{o.shipping_address.country_code}</div>}
                                    </div>
                                ) : (
                                    <span className="text-navy/40">{o.product_snapshot?.type === 'ebook' ? 'Digital' : '—'}</span>
                                )}
                            </td>
                            <td className="py-3 px-4"><div className="font-semibold text-navy">{o.product_snapshot?.title}</div><div className="text-xs text-navy/50">{o.product_snapshot?.type==='ebook'?'E-Book':'Physical'}</div></td>
                            <td className="py-3 px-4 text-gold font-bold">${parseFloat(o.amount).toFixed(2)}</td>
                            <td className="py-3 px-4 text-navy/60">{new Date(o.created_at).toLocaleDateString()}</td>
                            <td className="py-3 px-4">
                                {o.product_snapshot?.type === 'ebook' ? (
                                    <span className="text-xs bg-green-100 text-green-700 font-bold px-3 py-1 rounded-sm">Delivered (Digital)</span>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <select value={o.status} onChange={e=>updateStatus(o.order_id,e.target.value)} className="text-xs bg-white border border-gray-200 rounded px-2 py-1 text-navy focus:ring-1 focus:ring-gold" data-testid={`order-status-${o.order_id}`}>
                                            <option value="pending_fulfillment">Processing</option>
                                            <option value="shipped">Shipped</option>
                                            <option value="delivered">Delivered</option>
                                            <option value="cancelled">Cancelled</option>
                                        </select>
                                        {savedStatus[o.order_id] && <span className="text-green-600 text-xs font-bold">Saved</span>}
                                    </div>
                                )}
                            </td>
                            <td className="py-3 px-4 align-top" data-testid={`order-label-cell-${o.order_id}`}>
                                {o.product_snapshot?.type === 'ebook' ? (
                                    <span className="text-navy/40 text-xs">—</span>
                                ) : o.shippo_label_url ? (
                                    <div className="space-y-1">
                                        {o.tracking_number && (
                                            <div className="font-mono text-xs text-navy/80" data-testid={`order-tracking-${o.order_id}`}>{o.tracking_number}</div>
                                        )}
                                        <div className="flex flex-col gap-1">
                                            <a href={o.shippo_label_url} target="_blank" rel="noopener noreferrer" className="text-xs bg-navy text-white font-bold px-3 py-1 rounded hover:bg-navy/90 transition-colors text-center" data-testid={`reprint-label-btn-${o.order_id}`}>Reprint Label</a>
                                            <button type="button" onClick={()=>setLabelOrder(o)} className="text-xs text-navy/60 hover:text-navy underline" data-testid={`relabel-btn-${o.order_id}`}>New label</button>
                                        </div>
                                    </div>
                                ) : (['pending_fulfillment', 'processing'].includes(o.status)) ? (
                                    <button type="button" onClick={()=>setLabelOrder(o)} className="text-xs bg-gold text-navy font-bold px-3 py-2 rounded hover:bg-gold/90 transition-colors" data-testid={`print-label-btn-${o.order_id}`}>Print Label</button>
                                ) : (
                                    <span className="text-navy/40 text-xs">—</span>
                                )}
                            </td>
                        </tr>
                    ))}</tbody>
                </table>
                {filtered.length === 0 && <p className="font-body text-navy/60 text-center py-8">{search ? 'No orders match your search.' : 'No orders yet.'}</p>}
            </div>
            {labelOrder && <ShippingLabelModal order={labelOrder} onClose={()=>setLabelOrder(null)} onSuccess={()=>{ setLabelOrder(null); load(); }}/>}
        </div>
    );
}

// ─── SHIPPING LABEL MODAL ────────────────────────────────────────
function ShippingLabelModal({ order, onClose, onSuccess }) {
    const isHardcover = (order.product_snapshot?.title || '').toLowerCase().includes('hardcover');
    const [weight, setWeight] = useState(order.shipped_weight_oz ? String(order.shipped_weight_oz) : (isHardcover ? '24' : '12'));
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);

    // Editable shipping address — pre-populated from the order (which itself
    // pre-populates from the PayPal payer info at checkout). Admin can edit
    // and save any field before generating the label.
    const initialShip = order.shipping_address || {};
    const initialBC = order.buyer_contact || {};
    const initialRecipient = initialShip.recipient_name
        || `${initialBC.first_name || ''} ${initialBC.last_name || ''}`.trim()
        || order.customer_name
        || '';
    const [addr, setAddr] = useState({
        recipient_name: initialRecipient,
        line1: initialShip.line1 || '',
        line2: initialShip.line2 || '',
        city: initialShip.city || '',
        state: initialShip.state || '',
        postal_code: initialShip.postal_code || '',
        country_code: initialShip.country_code || 'US',
        phone: initialShip.phone || initialBC.phone || '',
    });
    const [editingAddr, setEditingAddr] = useState(!initialShip.line1);
    const [savingAddr, setSavingAddr] = useState(false);
    const [addrSaved, setAddrSaved] = useState(false);

    const saveAddress = async () => {
        setSavingAddr(true); setError('');
        try {
            await axios.patch(`${API}/admin/orders/${order.order_id}/shipping-address`, addr, { withCredentials: true });
            setEditingAddr(false);
            setAddrSaved(true);
            setTimeout(() => setAddrSaved(false), 2500);
        } catch (ex) {
            setError(ex.response?.data?.detail || 'Failed to save address.');
        } finally {
            setSavingAddr(false);
        }
    };

    const generate = async () => {
        // If the user has unsaved edits, save them first so the label is
        // generated against what they see on screen.
        if (editingAddr) {
            try {
                await axios.patch(`${API}/admin/orders/${order.order_id}/shipping-address`, addr, { withCredentials: true });
                setEditingAddr(false);
            } catch (ex) {
                setError(ex.response?.data?.detail || 'Please fix the shipping address first.');
                return;
            }
        }
        setGenerating(true); setError(''); setResult(null);
        try {
            const r = await axios.post(`${API}/admin/orders/${order.order_id}/generate-label`, { weight_oz: weight }, { withCredentials: true });
            setResult(r.data);
        } catch (ex) {
            setError(ex.response?.data?.detail || 'Failed to generate label.');
        } finally {
            setGenerating(false);
        }
    };

    const addrComplete = addr.recipient_name && addr.line1 && addr.city && addr.state && addr.postal_code;
    const inputCls = "w-full bg-white text-navy border border-gray-300 rounded-sm px-3 py-2 font-body text-sm focus:outline-none focus:ring-2 focus:ring-gold";

    return (
        <div className="fixed inset-0 bg-navy/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="shipping-label-modal">
            <div className="bg-white rounded-lg max-w-lg w-full p-6 space-y-5 shadow-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                    <h3 className="font-heading text-xl text-navy font-bold">{result ? 'Label Generated' : 'Print Shipping Label'}</h3>
                    <button type="button" onClick={onClose} className="text-navy/50 hover:text-navy text-2xl leading-none" data-testid="label-modal-close-btn">×</button>
                </div>

                {!result && (
                    <>
                        <div className="bg-ivory rounded p-4 font-body text-sm space-y-3" data-testid="label-modal-ship-to">
                            <div className="flex items-center justify-between">
                                <div className="text-navy/60 text-xs uppercase tracking-wider">Ship To</div>
                                {!editingAddr ? (
                                    <button type="button" onClick={()=>setEditingAddr(true)} className="text-xs text-gold hover:underline font-semibold" data-testid="label-edit-address-btn">Edit address</button>
                                ) : (
                                    <span className="text-xs text-navy/50">Editing…</span>
                                )}
                            </div>
                            {!editingAddr ? (
                                <div className="space-y-0.5">
                                    {addrComplete ? (
                                        <>
                                            <div className="font-semibold text-navy">{addr.recipient_name}</div>
                                            <div>{addr.line1}</div>
                                            {addr.line2 && <div>{addr.line2}</div>}
                                            <div>{[addr.city, addr.state, addr.postal_code].filter(Boolean).join(', ')}</div>
                                            {addr.phone && <div className="text-navy/60 text-xs">{addr.phone}</div>}
                                        </>
                                    ) : (
                                        <div className="text-red-600 font-semibold" data-testid="label-no-address">No shipping address on file. Please add one.</div>
                                    )}
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    <input type="text" placeholder="Recipient name *" value={addr.recipient_name} onChange={e=>setAddr({...addr,recipient_name:e.target.value})} className={inputCls + ' sm:col-span-2'} data-testid="label-addr-recipient-input"/>
                                    <input type="text" placeholder="Street Address Line 1 *" value={addr.line1} onChange={e=>setAddr({...addr,line1:e.target.value})} className={inputCls + ' sm:col-span-2'} data-testid="label-addr-line1-input"/>
                                    <input type="text" placeholder="Apt / Suite (optional)" value={addr.line2} onChange={e=>setAddr({...addr,line2:e.target.value})} className={inputCls + ' sm:col-span-2'} data-testid="label-addr-line2-input"/>
                                    <input type="text" placeholder="City *" value={addr.city} onChange={e=>setAddr({...addr,city:e.target.value})} className={inputCls} data-testid="label-addr-city-input"/>
                                    <div className="grid grid-cols-2 gap-2">
                                        <input type="text" maxLength={2} placeholder="State *" value={addr.state} onChange={e=>setAddr({...addr,state:e.target.value.toUpperCase()})} className={inputCls} data-testid="label-addr-state-input"/>
                                        <input type="text" placeholder="ZIP *" value={addr.postal_code} onChange={e=>setAddr({...addr,postal_code:e.target.value})} className={inputCls} data-testid="label-addr-zip-input"/>
                                    </div>
                                    <input type="tel" placeholder="Phone (optional)" value={addr.phone} onChange={e=>setAddr({...addr,phone:e.target.value})} className={inputCls + ' sm:col-span-2'} data-testid="label-addr-phone-input"/>
                                    <div className="sm:col-span-2 flex items-center justify-end gap-2 pt-1">
                                        {addrSaved && <span className="text-green-700 text-xs font-semibold">Saved ✓</span>}
                                        <button type="button" onClick={saveAddress} disabled={savingAddr} className="text-xs bg-navy text-white font-bold px-4 py-2 rounded hover:bg-navy/90 disabled:opacity-60" data-testid="label-save-address-btn">
                                            {savingAddr ? 'Saving…' : 'Save Address'}
                                        </button>
                                    </div>
                                </div>
                            )}
                            <div className="text-navy/60 text-xs mt-2 pt-2 border-t border-gray-200">
                                Product: <span className="text-navy font-semibold">{order.product_snapshot?.title}</span>
                            </div>
                        </div>

                        <div>
                            <label className="font-body text-navy/70 text-sm font-semibold block mb-1">Package Weight (oz) *</label>
                            <input type="number" min="0.1" step="0.1" value={weight} onChange={e=>setWeight(e.target.value)} className="w-full bg-white text-navy border border-gray-300 rounded-sm px-4 py-3 font-body text-base min-h-[44px] focus:outline-none focus:ring-2 focus:ring-gold" data-testid="label-weight-input"/>
                            <p className="font-body text-xs text-navy/60 mt-1">Default: 12 oz paperback / 24 oz hardcover. Edit if needed.</p>
                        </div>

                        {error && <p className="text-red-600 bg-red-50 border border-red-200 rounded p-3 font-body text-sm" data-testid="label-modal-error">{error}</p>}

                        <div className="flex gap-3 justify-end pt-2">
                            <button type="button" onClick={onClose} className="px-5 py-3 font-body text-navy/70 hover:text-navy transition-colors" data-testid="label-cancel-btn">Cancel</button>
                            <button type="button" onClick={generate} disabled={generating || !addrComplete} className="px-6 py-3 bg-gold text-navy font-body font-bold rounded hover:bg-gold/90 disabled:opacity-60 transition-colors" data-testid="label-generate-btn">
                                {generating ? 'Generating…' : 'Generate Label'}
                            </button>
                        </div>
                    </>
                )}

                {result && (
                    <div className="space-y-4">
                        <div className="bg-green-50 border border-green-200 rounded p-4 font-body text-sm">
                            <div className="font-bold text-green-800 mb-2" data-testid="label-success-banner">
                                ✓ {result.mode} label purchased — {result.rate_provider} {result.rate_service} (${result.rate_amount})
                            </div>
                            <div className="text-navy/70 text-xs uppercase tracking-wider mb-1 mt-3">Tracking Number</div>
                            <div className="font-mono text-base text-navy font-bold" data-testid="label-tracking-number">{result.tracking_number}</div>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <a href={result.label_url} target="_blank" rel="noopener noreferrer" className="flex-1 text-center px-5 py-3 bg-gold text-navy font-body font-bold rounded hover:bg-gold/90 transition-colors" data-testid="label-print-btn">Print Label (Open PDF)</a>
                            <a href={result.label_url} download className="flex-1 text-center px-5 py-3 bg-navy text-white font-body font-bold rounded hover:bg-navy/90 transition-colors" data-testid="label-download-btn">Download PDF</a>
                        </div>
                        <p className="font-body text-xs text-navy/60">Press Ctrl+P after the PDF opens. Select your Rongta 4×6 printer.</p>
                        <button type="button" onClick={onSuccess} className="w-full mt-2 px-5 py-3 border border-navy/20 text-navy font-body font-semibold rounded hover:bg-ivory transition-colors" data-testid="label-done-btn">Done</button>
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── CUSTOMERS ────────────────────────────────────────────────────
function AdminCustomers() {
    const [customers, setCustomers] = useState([]);
    const [customerOrders, setCustomerOrders] = useState(null);
    const [viewingCustomer, setViewingCustomer] = useState(null);
    const [confirmAction, setConfirmAction] = useState(null);
    const [msg, setMsg] = useState('');

    const load = () => axios.get(`${API}/admin/customers`, { withCredentials: true }).then(r=>setCustomers(r.data)).catch(()=>{});
    useEffect(() => { load(); }, []);

    const viewOrders = async (customer) => {
        setViewingCustomer(customer);
        const r = await axios.get(`${API}/admin/customers/${customer.user_id}/orders`, { withCredentials: true });
        setCustomerOrders(r.data);
    };

    const handleDeactivate = async (userId) => {
        try {
            await axios.patch(`${API}/admin/customers/${userId}/deactivate`, {}, { withCredentials: true });
            setMsg('Customer deactivated.'); setConfirmAction(null); load();
        } catch { setMsg('Failed to update customer.'); }
    };

    const handleReactivate = async (userId) => {
        try {
            await axios.patch(`${API}/admin/customers/${userId}/reactivate`, {}, { withCredentials: true });
            setMsg('Customer reactivated.'); load();
        } catch { setMsg('Failed to update customer.'); }
    };

    return (
        <div data-testid="admin-customers">
            <h2 className="font-heading text-2xl text-navy font-bold mb-8">Customers</h2>
            {msg && <p className="text-green-700 bg-green-50 border border-green-200 rounded p-3 font-body text-sm mb-4">{msg}</p>}
            <div className="overflow-x-auto bg-white rounded border border-gray-200">
                <table className="w-full font-body text-sm" data-testid="customers-table">
                    <thead><tr className="border-b border-gray-200 text-navy/60 text-left bg-ivory">
                        <th className="py-3 px-4">Name</th><th className="py-3 px-4">Email</th><th className="py-3 px-4">Joined</th><th className="py-3 px-4">Orders</th><th className="py-3 px-4">Status</th><th className="py-3 px-4">Actions</th>
                    </tr></thead>
                    <tbody>{customers.map(c=>(
                        <tr key={c.user_id} className="border-b border-gray-100">
                            <td className="py-3 px-4 font-semibold text-navy">{c.full_name}</td>
                            <td className="py-3 px-4 text-navy/70">{c.email}</td>
                            <td className="py-3 px-4 text-navy/60">{new Date(c.created_at).toLocaleDateString()}</td>
                            <td className="py-3 px-4 text-navy">{c.total_orders}</td>
                            <td className="py-3 px-4"><span className={`text-xs font-bold px-2 py-1 rounded ${c.is_active?'bg-green-100 text-green-700':'bg-red-100 text-red-600'}`}>{c.is_active?'Active':'Deactivated'}</span></td>
                            <td className="py-3 px-4">
                                <div className="flex items-center gap-2">
                                    <button onClick={()=>viewOrders(c)} className="text-navy/60 hover:text-gold transition-colors p-1" title="View Orders" data-testid={`view-orders-${c.user_id}`}><Eye size={16}/></button>
                                    {c.is_active ? (
                                        <button onClick={()=>setConfirmAction({user:c,action:'deactivate'})} className="text-xs bg-red-50 text-red-600 border border-red-200 px-2 py-1 rounded font-bold hover:bg-red-100 min-h-[32px]" data-testid={`deactivate-${c.user_id}`}>Deactivate</button>
                                    ) : (
                                        <button onClick={()=>handleReactivate(c.user_id)} className="text-xs bg-green-50 text-green-700 border border-green-200 px-2 py-1 rounded font-bold hover:bg-green-100 min-h-[32px]" data-testid={`reactivate-${c.user_id}`}>Reactivate</button>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}</tbody>
                </table>
                {customers.length === 0 && <p className="font-body text-navy/60 text-center py-8">No customers yet.</p>}
            </div>

            {/* Customer Orders Modal */}
            {viewingCustomer && (
                <div className="fixed inset-0 bg-navy/80 backdrop-blur flex items-center justify-center z-50 p-4" data-testid="customer-orders-modal">
                    <div className="bg-white rounded max-w-2xl w-full p-8 max-h-[80vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="font-heading text-xl text-navy font-bold">Orders — {viewingCustomer.full_name}</h3>
                            <button onClick={()=>{setViewingCustomer(null);setCustomerOrders(null);}} className="text-navy/40 hover:text-navy"><X size={20}/></button>
                        </div>
                        {!customerOrders ? <p className="font-body text-navy/60">Loading...</p> : customerOrders.length === 0 ? <p className="font-body text-navy/60">No orders found.</p> : (
                            <table className="w-full font-body text-sm">
                                <thead><tr className="border-b text-navy/60 text-left"><th className="py-2 pr-4">Date</th><th className="py-2 pr-4">Product</th><th className="py-2 pr-4">Amount</th><th className="py-2">Status</th></tr></thead>
                                <tbody>{customerOrders.map(o=>(
                                    <tr key={o.order_id} className="border-b border-gray-100">
                                        <td className="py-3 pr-4 text-navy/60">{new Date(o.created_at).toLocaleDateString()}</td>
                                        <td className="py-3 pr-4 text-navy font-semibold">{o.product_snapshot?.title}</td>
                                        <td className="py-3 pr-4 text-gold font-bold">${parseFloat(o.amount).toFixed(2)}</td>
                                        <td className="py-3"><span className="text-xs bg-gray-100 text-navy/70 px-2 py-1 rounded">{o.status}</span></td>
                                    </tr>
                                ))}</tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}

            {/* Deactivate Confirm */}
            {confirmAction && (
                <div className="fixed inset-0 bg-navy/80 backdrop-blur flex items-center justify-center z-50 p-4" data-testid="deactivate-confirm-modal">
                    <div className="bg-white rounded p-8 max-w-sm w-full">
                        <h3 className="font-heading text-xl text-navy font-bold mb-4">Deactivate Customer</h3>
                        <p className="font-body text-navy/70 mb-6">Deactivate <strong>{confirmAction.user.full_name}</strong>? Their data is preserved but they will not be able to log in.</p>
                        <div className="flex gap-4">
                            <button onClick={()=>handleDeactivate(confirmAction.user.user_id)} className="bg-red-600 text-white font-bold px-6 py-2 rounded font-body min-h-[44px]" data-testid="confirm-deactivate-btn">Deactivate</button>
                            <button onClick={()=>setConfirmAction(null)} className="bg-gray-100 text-navy font-bold px-6 py-2 rounded font-body min-h-[44px]">Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── SETTINGS ─────────────────────────────────────────────────────
function AdminSettings() {
    const [form, setForm] = useState({
        paypal_client_id:'', paypal_secret:'', paypal_mode:'sandbox', paypal_webhook_id:'',
        from_email:'', smtp_host:'', smtp_port:'587', smtp_username:'', smtp_password:'',
        twilio_account_sid:'', twilio_auth_token:'', twilio_from_number:'',
        ebook_download_limit:'5', ebook_expiry_days:'30',
        email_attachment_enabled:'true', sms_delivery_enabled:'false',
        // Shipping (Shippo + ship-from)
        shippo_api_key:'',
        shipfrom_name:'', shipfrom_company:'', shipfrom_street1:'', shipfrom_street2:'',
        shipfrom_city:'', shipfrom_state:'', shipfrom_zip:'', shipfrom_phone:'', shipfrom_email:'',
        // Bunny.net media hosting
        bunny_storage_zone:'', bunny_password:'', bunny_pull_zone:'', bunny_endpoint:'',
    });
    const [msg, setMsg] = useState('');
    const [err, setErr] = useState('');
    const [loading, setLoading] = useState(false);
    // Shippo test connection
    const [shippoTesting, setShippoTesting] = useState(false);
    const [shippoTestMsg, setShippoTestMsg] = useState({ type: '', text: '' });

    useEffect(() => {
        axios.get(`${API}/admin/settings`, { withCredentials: true }).then(r=>{
            const d = r.data;
            // PayPal credentials always come back masked (••••••••<last 4>)
            // until the admin enters the unlock passcode. Both fields are
            // shown using the masked value so the admin can confirm the
            // last 4 chars without revealing the full credential.
            setPaypalDisplay({
                client_id: d.paypal_client_id || '',
                secret: d.paypal_secret || '',
            });
            setForm(prev=>({
                ...prev,
                paypal_mode: d.paypal_mode || 'sandbox',
                paypal_webhook_id: d.paypal_webhook_id || '',
                from_email: d.from_email || '',
                smtp_host: d.smtp_host || '',
                smtp_port: d.smtp_port || '587',
                smtp_username: d.smtp_username || '',
                smtp_password: '',
                twilio_account_sid: d.twilio_account_sid || '',
                twilio_auth_token: '',
                twilio_from_number: d.twilio_from_number || '',
                ebook_download_limit: d.ebook_download_limit || '5',
                ebook_expiry_days: d.ebook_expiry_days || '30',
                email_attachment_enabled: d.email_attachment_enabled || 'true',
                sms_delivery_enabled: d.sms_delivery_enabled || 'false',
                // Shipping — Shippo key is masked by backend so we just keep
                // what comes back; ship-from fields are plaintext.
                shippo_api_key: d.shippo_api_key || '',
                shipfrom_name: d.shipfrom_name || '',
                shipfrom_company: d.shipfrom_company || '',
                shipfrom_street1: d.shipfrom_street1 || '',
                shipfrom_street2: d.shipfrom_street2 || '',
                shipfrom_city: d.shipfrom_city || '',
                shipfrom_state: d.shipfrom_state || '',
                shipfrom_zip: d.shipfrom_zip || '',
                shipfrom_phone: d.shipfrom_phone || '',
                shipfrom_email: d.shipfrom_email || '',
                bunny_storage_zone: d.bunny_storage_zone || '',
                bunny_password: d.bunny_password || '',
                bunny_pull_zone: d.bunny_pull_zone || '',
                bunny_endpoint: d.bunny_endpoint || '',
            }));
        }).catch(()=>{});
    }, []);

    // PayPal credential reveal/edit — masked by default; full value shown
    // only after admin enters the unlock passcode 9670. Once unlocked, both
    // fields become editable. We use a single display state so the inputs
    // always render exactly what the admin should see.
    const [paypalRevealed, setPaypalRevealed] = useState(false);
    const [paypalDisplay, setPaypalDisplay] = useState({ client_id: '', secret: '' }); // masked OR full depending on revealed
    const [paypalMaskedSnapshot, setPaypalMaskedSnapshot] = useState({ client_id: '', secret: '' });
    const [showPasscodeModal, setShowPasscodeModal] = useState(false);
    const [passcodeInput, setPasscodeInput] = useState('');
    const [passcodeError, setPasscodeError] = useState('');

    // Keep the masked snapshot in sync with whatever the GET returned.
    useEffect(() => {
        if (!paypalRevealed) {
            setPaypalMaskedSnapshot(paypalDisplay);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paypalDisplay.client_id, paypalDisplay.secret]);

    const handleUnlockPaypal = async () => {
        setPasscodeError('');
        try {
            const r = await axios.post(`${API}/admin/settings/reveal`,
                { passcode: passcodeInput },
                { withCredentials: true });
            // Snapshot the current masked values so we can restore them on Hide.
            setPaypalMaskedSnapshot(paypalDisplay);
            setPaypalDisplay({
                client_id: r.data.paypal_client_id || '',
                secret: r.data.paypal_secret || '',
            });
            setPaypalRevealed(true);
            setShowPasscodeModal(false);
            setPasscodeInput('');
        } catch (ex) {
            setPasscodeError(ex.response?.data?.detail || 'Invalid passcode');
        }
    };
    const handleHidePaypal = () => {
        setPaypalRevealed(false);
        // Restore masked display
        setPaypalDisplay(paypalMaskedSnapshot);
    };

    const handleSave = async (e) => {
        e.preventDefault(); setMsg(''); setErr(''); setLoading(true);
        try {
            const payload = { ...form };
            // Only include PayPal credentials when revealed/edited. The
            // masked display value is never sent to the backend.
            if (paypalRevealed) {
                if (paypalDisplay.client_id && !paypalDisplay.client_id.startsWith('••••')) {
                    payload.paypal_client_id = paypalDisplay.client_id;
                }
                if (paypalDisplay.secret && !paypalDisplay.secret.startsWith('••••')) {
                    payload.paypal_secret = paypalDisplay.secret;
                }
            }
            if (!payload.paypal_secret) delete payload.paypal_secret;
            if (!payload.paypal_client_id) delete payload.paypal_client_id;
            if (!payload.smtp_password) delete payload.smtp_password;
            if (!payload.twilio_auth_token) delete payload.twilio_auth_token;
            // Don't send the masked Shippo key back as the new value
            if (!payload.shippo_api_key || payload.shippo_api_key.startsWith('••••')) delete payload.shippo_api_key;
            // Don't send masked bunny password either
            if (!payload.bunny_password || payload.bunny_password.startsWith('••••')) delete payload.bunny_password;
            await axios.post(`${API}/admin/settings`, payload, { withCredentials: true });
            setMsg('Settings saved successfully.');
            setForm(prev => ({ ...prev, smtp_password: '', twilio_auth_token: '' }));
        } catch(ex) { setErr(ex.response?.data?.detail || 'Failed to save settings.'); }
        finally { setLoading(false); }
    };

    const inputCls = "w-full bg-white text-navy border border-gray-300 rounded-sm px-4 py-3 font-body text-base min-h-[44px] focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent";
    const labelCls = "font-body text-navy/70 text-sm font-semibold block mb-1";

    const handleTestShippo = async () => {
        setShippoTesting(true); setShippoTestMsg({ type: '', text: '' });
        try {
            if (form.shippo_api_key && !form.shippo_api_key.startsWith('••••')) {
                await axios.post(`${API}/admin/settings`, { shippo_api_key: form.shippo_api_key }, { withCredentials: true });
            }
            const r = await axios.post(`${API}/admin/shippo/test-connection`, {}, { withCredentials: true });
            setShippoTestMsg({ type: 'success', text: r.data.message || 'Connection successful.' });
        } catch (ex) {
            setShippoTestMsg({ type: 'error', text: ex.response?.data?.detail || 'Connection failed.' });
        } finally {
            setShippoTesting(false);
        }
    };

    // Bunny.net test connection + migration
    const [bunnyTesting, setBunnyTesting] = useState(false);
    const [bunnyTestMsg, setBunnyTestMsg] = useState({ type: '', text: '' });
    const [bunnyMigrating, setBunnyMigrating] = useState(false);
    const [bunnyMigrateReport, setBunnyMigrateReport] = useState(null);

    const saveBunnyIfDirty = async () => {
        const payload = {};
        if (form.bunny_storage_zone) payload.bunny_storage_zone = form.bunny_storage_zone;
        if (form.bunny_pull_zone) payload.bunny_pull_zone = form.bunny_pull_zone;
        if (form.bunny_endpoint) payload.bunny_endpoint = form.bunny_endpoint;
        if (form.bunny_password && !form.bunny_password.startsWith('••••')) payload.bunny_password = form.bunny_password;
        if (Object.keys(payload).length === 0) return;
        await axios.post(`${API}/admin/settings`, payload, { withCredentials: true });
    };

    const handleTestBunny = async () => {
        setBunnyTesting(true); setBunnyTestMsg({ type: '', text: '' });
        try {
            await saveBunnyIfDirty();
            const r = await axios.post(`${API}/admin/bunny/test-connection`, {}, { withCredentials: true });
            setBunnyTestMsg({ type: 'success', text: r.data.message || 'Connection successful.' });
        } catch (ex) {
            setBunnyTestMsg({ type: 'error', text: ex.response?.data?.detail || 'Connection failed.' });
        } finally {
            setBunnyTesting(false);
        }
    };

    const handleMigrateBunny = async () => {
        if (!window.confirm('Copy all existing photos & videos from Emergent storage to bunny.net? This is safe to run multiple times.')) return;
        setBunnyMigrating(true); setBunnyMigrateReport(null);
        try {
            await saveBunnyIfDirty();
            const r = await axios.post(`${API}/admin/bunny/migrate-media`, {}, { withCredentials: true });
            setBunnyMigrateReport(r.data);
        } catch (ex) {
            setBunnyMigrateReport({ error: ex.response?.data?.detail || 'Migration failed.' });
        } finally {
            setBunnyMigrating(false);
        }
    };

    return (
        <div data-testid="admin-settings">
            <h2 className="font-heading text-2xl text-navy font-bold mb-8">Settings</h2>
            <form onSubmit={handleSave} className="max-w-2xl space-y-10">
                {/* PayPal */}
                <div className="bg-white rounded border border-gray-200 p-6 space-y-5">
                    <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                        <h3 className="font-heading text-xl text-navy font-semibold">PayPal Configuration</h3>
                        {paypalRevealed ? (
                            <button type="button" onClick={handleHidePaypal} className="font-body text-sm text-navy/60 hover:text-navy underline" data-testid="paypal-hide-btn">Hide credentials</button>
                        ) : (
                            <button type="button" onClick={() => { setShowPasscodeModal(true); setPasscodeInput(''); setPasscodeError(''); }} className="font-body text-sm text-gold hover:text-navy underline" data-testid="paypal-reveal-btn">Show credentials</button>
                        )}
                    </div>
                    <div>
                        <label className={labelCls}>PayPal Client ID {!paypalRevealed && <span className="text-xs text-navy/50 font-normal">(showing last 4)</span>}</label>
                        <input type="text"
                               value={paypalDisplay.client_id}
                               readOnly={!paypalRevealed}
                               onChange={e=>setPaypalDisplay({...paypalDisplay, client_id: e.target.value})}
                               className={inputCls + (paypalRevealed ? '' : ' bg-gray-50 text-navy/70 cursor-not-allowed font-mono')}
                               data-testid="paypal-client-id-input"/>
                    </div>
                    <div>
                        <label className={labelCls}>PayPal Secret {!paypalRevealed && <span className="text-xs text-navy/50 font-normal">(showing last 4)</span>}</label>
                        <input type="text"
                               value={paypalDisplay.secret}
                               readOnly={!paypalRevealed}
                               onChange={e=>setPaypalDisplay({...paypalDisplay, secret: e.target.value})}
                               className={inputCls + (paypalRevealed ? '' : ' bg-gray-50 text-navy/70 cursor-not-allowed font-mono')}
                               placeholder={paypalRevealed ? 'Enter to update' : ''}
                               data-testid="paypal-secret-input"/>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div><label className={labelCls}>PayPal Mode</label>
                            <select value={form.paypal_mode} onChange={e=>setForm({...form,paypal_mode:e.target.value})} className={inputCls} data-testid="paypal-mode-select">
                                <option value="sandbox">Sandbox</option><option value="live">Live</option>
                            </select>
                        </div>
                        <div><label className={labelCls}>PayPal Webhook ID</label><input type="text" value={form.paypal_webhook_id} onChange={e=>setForm({...form,paypal_webhook_id:e.target.value})} className={inputCls} placeholder="Required for refund/dispute handling" data-testid="paypal-webhook-id-input"/></div>
                    </div>
                </div>

                {/* PayPal Reveal Passcode Modal */}
                {showPasscodeModal && (
                    <div className="fixed inset-0 bg-navy/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="paypal-passcode-modal">
                        <div className="bg-white rounded-lg max-w-md w-full p-6 space-y-4 shadow-2xl">
                            <h4 className="font-heading text-xl text-navy font-bold">Unlock PayPal Credentials</h4>
                            <p className="font-body text-navy/70 text-sm">Enter the admin passcode to view full PayPal credentials.</p>
                            <input
                                type="password"
                                value={passcodeInput}
                                onChange={e=>{ setPasscodeInput(e.target.value); setPasscodeError(''); }}
                                onKeyDown={e=>{ if (e.key === 'Enter') handleUnlockPaypal(); }}
                                autoFocus
                                placeholder="Passcode"
                                className={inputCls}
                                data-testid="paypal-passcode-input"
                            />
                            {passcodeError && <p className="font-body text-red-600 text-sm" data-testid="paypal-passcode-error">{passcodeError}</p>}
                            <div className="flex gap-3 justify-end pt-2">
                                <button type="button" onClick={()=>{ setShowPasscodeModal(false); setPasscodeInput(''); setPasscodeError(''); }} className="px-5 py-2 font-body text-navy/70 hover:text-navy transition-colors" data-testid="paypal-passcode-cancel-btn">Cancel</button>
                                <button type="button" onClick={handleUnlockPaypal} className="px-5 py-2 bg-gold text-navy font-body font-semibold rounded hover:bg-gold/90 transition-colors" data-testid="paypal-passcode-submit-btn">Unlock</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* E-Book Delivery */}
                <div className="bg-white rounded border border-gray-200 p-6 space-y-5">
                    <h3 className="font-heading text-xl text-navy font-semibold border-b border-gray-100 pb-3">E-Book Delivery</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div><label className={labelCls}>PDF Download Limit</label><input type="number" min="1" value={form.ebook_download_limit} onChange={e=>setForm({...form,ebook_download_limit:e.target.value})} className={inputCls} data-testid="ebook-download-limit-input"/></div>
                        <div><label className={labelCls}>Download Expiry (days)</label><input type="number" min="1" value={form.ebook_expiry_days} onChange={e=>setForm({...form,ebook_expiry_days:e.target.value})} className={inputCls} data-testid="ebook-expiry-days-input"/></div>
                    </div>
                    <div className="flex items-center gap-6">
                        <label className="flex items-center gap-2 font-body text-sm text-navy cursor-pointer"><input type="checkbox" checked={form.email_attachment_enabled === 'true'} onChange={e=>setForm({...form,email_attachment_enabled:e.target.checked?'true':'false'})} className="accent-gold w-4 h-4"/> Email PDF Attachment Enabled</label>
                        <label className="flex items-center gap-2 font-body text-sm text-navy cursor-pointer"><input type="checkbox" checked={form.sms_delivery_enabled === 'true'} onChange={e=>setForm({...form,sms_delivery_enabled:e.target.checked?'true':'false'})} className="accent-gold w-4 h-4"/> SMS Delivery Enabled</label>
                    </div>
                </div>

                {/* Email */}
                <div className="bg-white rounded border border-gray-200 p-6 space-y-5">
                    <h3 className="font-heading text-xl text-navy font-semibold border-b border-gray-100 pb-3">Email (SMTP)</h3>
                    <div><label className={labelCls}>From Email</label><input type="email" value={form.from_email} onChange={e=>setForm({...form,from_email:e.target.value})} className={inputCls} data-testid="from-email-input"/></div>
                    <div><label className={labelCls}>SMTP Host</label><input type="text" value={form.smtp_host} onChange={e=>setForm({...form,smtp_host:e.target.value})} className={inputCls} data-testid="smtp-host-input"/></div>
                    <div className="grid grid-cols-2 gap-4">
                        <div><label className={labelCls}>SMTP Port</label><input type="number" value={form.smtp_port} onChange={e=>setForm({...form,smtp_port:e.target.value})} className={inputCls} data-testid="smtp-port-input"/></div>
                        <div><label className={labelCls}>SMTP Username</label><input type="text" value={form.smtp_username} onChange={e=>setForm({...form,smtp_username:e.target.value})} className={inputCls} data-testid="smtp-username-input"/></div>
                    </div>
                    <div><label className={labelCls}>SMTP Password</label><input type="password" value={form.smtp_password} onChange={e=>setForm({...form,smtp_password:e.target.value})} className={inputCls} placeholder="Enter to update" data-testid="smtp-password-input"/></div>
                </div>

                {/* Twilio */}
                <div className="bg-white rounded border border-gray-200 p-6 space-y-5">
                    <h3 className="font-heading text-xl text-navy font-semibold border-b border-gray-100 pb-3">Twilio (SMS)</h3>
                    <p className="font-body text-navy/60 text-sm bg-yellow-50 border border-yellow-200 rounded p-3">SMS delivery requires Twilio credentials. Contact support if you need help setting this up.</p>
                    <div><label className={labelCls}>Twilio Account SID</label><input type="text" value={form.twilio_account_sid} onChange={e=>setForm({...form,twilio_account_sid:e.target.value})} className={inputCls} data-testid="twilio-sid-input"/></div>
                    <div><label className={labelCls}>Twilio Auth Token</label><input type="password" value={form.twilio_auth_token} onChange={e=>setForm({...form,twilio_auth_token:e.target.value})} className={inputCls} placeholder="Enter to update" data-testid="twilio-token-input"/></div>
                    <div><label className={labelCls}>Twilio From Number</label><input type="tel" value={form.twilio_from_number} onChange={e=>setForm({...form,twilio_from_number:e.target.value})} className={inputCls} placeholder="+15551234567" data-testid="twilio-number-input"/></div>
                </div>

                {/* Shipping (Shippo + USPS) */}
                <div className="bg-white rounded border border-gray-200 p-6 space-y-5" data-testid="settings-shipping-section">
                    <h3 className="font-heading text-xl text-navy font-semibold border-b border-gray-100 pb-3">Shipping (Shippo + USPS)</h3>

                    <div>
                        <label className={labelCls}>Shippo API Key</label>
                        <input type="password"
                               value={form.shippo_api_key}
                               onChange={e=>setForm({...form, shippo_api_key: e.target.value})}
                               className={inputCls}
                               placeholder={form.shippo_api_key && form.shippo_api_key.startsWith('••••') ? '' : 'shippo_test_… or shippo_live_…'}
                               data-testid="shippo-api-key-input"/>
                        <p className="font-body text-xs text-navy/60 mt-2">
                            Paste your Shippo Test token now. When your Live token arrives by email, replace it here to ship real labels.
                        </p>
                        <div className="flex items-center gap-3 mt-3">
                            <button type="button" onClick={handleTestShippo} disabled={shippoTesting}
                                    className="px-5 py-2 bg-navy text-white font-body font-semibold rounded hover:bg-navy/90 disabled:opacity-60 transition-colors text-sm"
                                    data-testid="shippo-test-connection-btn">
                                {shippoTesting ? 'Testing…' : 'Test Connection'}
                            </button>
                            {shippoTestMsg.text && (
                                <span className={`font-body text-sm ${shippoTestMsg.type === 'success' ? 'text-green-700' : 'text-red-600'}`} data-testid="shippo-test-result">
                                    {shippoTestMsg.text}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="pt-2 border-t border-gray-100">
                        <h4 className="font-heading text-base text-navy font-semibold mb-3 mt-2">Ship-From Address</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div><label className={labelCls}>Sender Full Name *</label><input type="text" value={form.shipfrom_name} onChange={e=>setForm({...form,shipfrom_name:e.target.value})} className={inputCls} data-testid="shipfrom-name-input"/></div>
                            <div><label className={labelCls}>Company (optional)</label><input type="text" value={form.shipfrom_company} onChange={e=>setForm({...form,shipfrom_company:e.target.value})} className={inputCls} data-testid="shipfrom-company-input"/></div>
                            <div className="md:col-span-2"><label className={labelCls}>Street Address Line 1 *</label><input type="text" value={form.shipfrom_street1} onChange={e=>setForm({...form,shipfrom_street1:e.target.value})} className={inputCls} data-testid="shipfrom-street1-input"/></div>
                            <div className="md:col-span-2"><label className={labelCls}>Street Address Line 2 (optional)</label><input type="text" value={form.shipfrom_street2} onChange={e=>setForm({...form,shipfrom_street2:e.target.value})} className={inputCls} data-testid="shipfrom-street2-input"/></div>
                            <div><label className={labelCls}>City *</label><input type="text" value={form.shipfrom_city} onChange={e=>setForm({...form,shipfrom_city:e.target.value})} className={inputCls} data-testid="shipfrom-city-input"/></div>
                            <div className="grid grid-cols-2 gap-3">
                                <div><label className={labelCls}>State *</label><input type="text" maxLength={2} value={form.shipfrom_state} onChange={e=>setForm({...form,shipfrom_state:e.target.value.toUpperCase()})} className={inputCls} placeholder="MS" data-testid="shipfrom-state-input"/></div>
                                <div><label className={labelCls}>ZIP *</label><input type="text" value={form.shipfrom_zip} onChange={e=>setForm({...form,shipfrom_zip:e.target.value})} className={inputCls} placeholder="39157" data-testid="shipfrom-zip-input"/></div>
                            </div>
                            <div><label className={labelCls}>Sender Phone *</label><input type="tel" value={form.shipfrom_phone} onChange={e=>setForm({...form,shipfrom_phone:e.target.value})} className={inputCls} placeholder="6015551212" data-testid="shipfrom-phone-input"/></div>
                            <div><label className={labelCls}>Sender Email</label><input type="email" value={form.shipfrom_email} onChange={e=>setForm({...form,shipfrom_email:e.target.value})} className={inputCls} placeholder="(defaults to admin email)" data-testid="shipfrom-email-input"/></div>
                        </div>
                        <p className="font-body text-xs text-navy/60 mt-3">Country: US (USPS shipping only)</p>
                    </div>
                </div>

                {/* Media Hosting (bunny.net) */}
                <div className="bg-white rounded border border-gray-200 p-6 space-y-5" data-testid="settings-bunny-section">
                    <h3 className="font-heading text-xl text-navy font-semibold border-b border-gray-100 pb-3">Media Hosting (bunny.net)</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div><label className={labelCls}>Storage Zone Name</label><input type="text" value={form.bunny_storage_zone} onChange={e=>setForm({...form,bunny_storage_zone:e.target.value})} className={inputCls} data-testid="bunny-zone-input"/></div>
                        <div><label className={labelCls}>Pull Zone Hostname</label><input type="text" value={form.bunny_pull_zone} onChange={e=>setForm({...form,bunny_pull_zone:e.target.value})} className={inputCls} placeholder="KYS-MEDIA-PULL.b-cdn.net" data-testid="bunny-pull-input"/></div>
                        <div className="md:col-span-2"><label className={labelCls}>Storage Password (API Key)</label>
                            <input type="password" value={form.bunny_password} onChange={e=>setForm({...form,bunny_password:e.target.value})} className={inputCls} placeholder={form.bunny_password && form.bunny_password.startsWith('••••') ? '' : 'Paste from bunny.net → FTP & API Access'} data-testid="bunny-password-input"/>
                        </div>
                        <div className="md:col-span-2"><label className={labelCls}>Storage Region Endpoint</label>
                            <input type="text" value={form.bunny_endpoint} onChange={e=>setForm({...form,bunny_endpoint:e.target.value})} className={inputCls} placeholder="storage.bunnycdn.com" data-testid="bunny-endpoint-input"/>
                            <p className="font-body text-xs text-navy/60 mt-1">Default Frankfurt: <code>storage.bunnycdn.com</code>. Other regions: <code>ny.</code>, <code>la.</code>, <code>sg.</code>, <code>uk.</code>, <code>se.</code>, <code>br.</code>, <code>jh.</code>, <code>syd.</code> — find the right prefix in your bunny.net Storage Zone dashboard.</p>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 pt-2">
                        <button type="button" onClick={handleTestBunny} disabled={bunnyTesting} className="px-5 py-2 bg-navy text-white font-body font-semibold rounded hover:bg-navy/90 disabled:opacity-60 transition-colors text-sm" data-testid="bunny-test-connection-btn">
                            {bunnyTesting ? 'Testing…' : 'Test Connection'}
                        </button>
                        <button type="button" onClick={handleMigrateBunny} disabled={bunnyMigrating} className="px-5 py-2 bg-ivory border border-gold text-navy font-body font-semibold rounded hover:bg-gold/10 disabled:opacity-60 transition-colors text-sm" data-testid="bunny-migrate-btn">
                            {bunnyMigrating ? 'Migrating…' : 'Migrate Existing Media'}
                        </button>
                        {bunnyTestMsg.text && (
                            <span className={`font-body text-sm ${bunnyTestMsg.type === 'success' ? 'text-green-700' : 'text-red-600'}`} data-testid="bunny-test-result">
                                {bunnyTestMsg.text}
                            </span>
                        )}
                    </div>
                    {bunnyMigrateReport && (
                        <div className="bg-ivory border border-gold/20 rounded p-3 font-body text-xs text-navy" data-testid="bunny-migrate-report">
                            {bunnyMigrateReport.error ? (
                                <p className="text-red-600">{bunnyMigrateReport.error}</p>
                            ) : (
                                <>
                                    <p>Photos: {bunnyMigrateReport.photos_migrated}/{bunnyMigrateReport.photos_found} migrated.</p>
                                    <p>Videos: {bunnyMigrateReport.videos_migrated}/{bunnyMigrateReport.videos_found} migrated.</p>
                                    {(bunnyMigrateReport.failures || []).length > 0 && (
                                        <details className="mt-2"><summary className="cursor-pointer text-red-600">{bunnyMigrateReport.failures.length} failures</summary>
                                            <pre className="text-[10px] mt-1 overflow-x-auto">{JSON.stringify(bunnyMigrateReport.failures, null, 2)}</pre>
                                        </details>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>

                {msg && <p className="text-green-700 bg-green-50 border border-green-200 rounded p-3 font-body" data-testid="settings-success">{msg}</p>}
                {err && <p className="text-red-600 bg-red-50 border border-red-200 rounded p-3 font-body" data-testid="settings-error">{err}</p>}
                <button type="submit" disabled={loading} className="bg-gold hover:bg-gold-hover disabled:opacity-50 text-navy font-bold px-10 py-3 rounded-sm font-body text-lg transition-colors min-h-[44px]" data-testid="settings-save-btn">
                    {loading ? 'Saving...' : 'Save Settings'}
                </button>
            </form>
        </div>
    );
}

// ─── ADMIN ANALYTICS ──────────────────────────────────────────────
function AdminAnalytics() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        axios.get(`${API}/admin/analytics`, { withCredentials: true })
            .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    }, []);
    if (loading) return <p className="font-body text-navy/60">Loading analytics...</p>;
    if (!data) return <p className="font-body text-red-600">Failed to load analytics.</p>;
    const maxRevenue = Math.max(...data.monthly_revenue.map(m => m.revenue), 1);
    return (
        <div data-testid="admin-analytics">
            <h2 className="font-heading text-3xl text-navy font-bold mb-8">Sales Analytics</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                {[['Revenue', `$${data.total_revenue.toFixed(2)}`],['Orders', data.total_orders],['Customers', data.total_customers],['Products', data.total_products]].map(([l,v])=>(
                    <div key={l} className="bg-white rounded-lg border border-gray-200 p-6" data-testid={`analytics-${l.toLowerCase()}`}>
                        <p className="font-body text-navy/50 text-sm">{l}</p>
                        <p className="font-heading text-2xl font-bold text-navy mt-1">{v}</p>
                    </div>
                ))}
            </div>
            {data.monthly_revenue.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-6 mb-10">
                    <h3 className="font-heading text-xl font-bold text-navy mb-4">Monthly Revenue</h3>
                    <div className="flex items-end gap-2 h-48" data-testid="monthly-revenue-chart">
                        {data.monthly_revenue.map(m => (
                            <div key={m.month} className="flex-1 flex flex-col items-center justify-end gap-1">
                                <span className="font-body text-xs text-navy/60">${m.revenue.toFixed(0)}</span>
                                <div className="w-full bg-gold rounded-t" style={{ height: `${(m.revenue / maxRevenue) * 160}px`, minHeight: '4px' }}></div>
                                <span className="font-body text-xs text-navy/50">{m.month.slice(5)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {data.top_products.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="font-heading text-xl font-bold text-navy mb-4">Top Products</h3>
                    <table className="w-full font-body text-sm">
                        <thead><tr className="border-b text-navy/60 text-left"><th className="py-2">Product</th><th className="py-2">Sales</th><th className="py-2">Revenue</th></tr></thead>
                        <tbody>{data.top_products.map((p,i) => (
                            <tr key={i} className="border-b border-gray-50">
                                <td className="py-3 font-semibold text-navy">{p.title}</td>
                                <td className="py-3 text-navy/70">{p.count}</td>
                                <td className="py-3 text-gold font-bold">${p.revenue.toFixed(2)}</td>
                            </tr>
                        ))}</tbody>
                    </table>
                </div>
            )}
        </div>
    );
}


// ─── ADMIN COUPONS ────────────────────────────────────────────────
function AdminCoupons() {
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ code: '', discount_type: 'percentage', discount_value: '', max_uses: '-1', expires_at: '' });
    const [err, setErr] = useState('');
    const [msg, setMsg] = useState('');
    const load = () => axios.get(`${API}/admin/coupons`, { withCredentials: true }).then(r => setCoupons(r.data)).catch(() => {}).finally(() => setLoading(false));
    useEffect(() => { load(); }, []);

    const handleCreate = async (e) => {
        e.preventDefault(); setErr(''); setMsg('');
        try {
            await axios.post(`${API}/admin/coupons`, {
                code: form.code, discount_type: form.discount_type,
                discount_value: parseFloat(form.discount_value),
                max_uses: parseInt(form.max_uses),
                expires_at: form.expires_at || null
            }, { withCredentials: true });
            setMsg('Coupon created!'); setShowForm(false);
            setForm({ code: '', discount_type: 'percentage', discount_value: '', max_uses: '-1', expires_at: '' });
            load();
        } catch (e) { setErr(e.response?.data?.detail || 'Failed to create coupon'); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this coupon?')) return;
        await axios.delete(`${API}/admin/coupons/${id}`, { withCredentials: true });
        load();
    };

    const inputCls = "w-full bg-white text-navy border border-gray-300 rounded-sm px-4 py-3 font-body text-base min-h-[44px] focus:outline-none focus:ring-2 focus:ring-gold";
    const labelCls = "font-body text-navy/70 text-sm font-semibold block mb-1";

    return (
        <div data-testid="admin-coupons">
            <div className="flex items-center justify-between mb-8">
                <h2 className="font-heading text-3xl text-navy font-bold">Coupons</h2>
                <button onClick={() => { setShowForm(!showForm); setErr(''); setMsg(''); }} className="bg-gold hover:bg-gold-hover text-navy font-bold px-6 py-3 rounded-sm font-body min-h-[44px]" data-testid="add-coupon-btn">
                    {showForm ? 'Cancel' : '+ New Coupon'}
                </button>
            </div>
            {msg && <p className="text-green-700 text-sm font-body bg-green-50 border border-green-200 rounded p-3 mb-4">{msg}</p>}
            {showForm && (
                <form onSubmit={handleCreate} className="bg-white rounded-lg border border-gray-200 p-6 mb-8 space-y-4" data-testid="coupon-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div><label className={labelCls}>Coupon Code</label><input type="text" required value={form.code} onChange={e=>setForm({...form, code: e.target.value.toUpperCase()})} className={inputCls} placeholder="e.g. SAVE20" data-testid="coupon-code-input"/></div>
                        <div><label className={labelCls}>Discount Type</label>
                            <select value={form.discount_type} onChange={e=>setForm({...form, discount_type: e.target.value})} className={inputCls} data-testid="coupon-type-select">
                                <option value="percentage">Percentage (%)</option>
                                <option value="fixed">Fixed Amount ($)</option>
                            </select>
                        </div>
                        <div><label className={labelCls}>Discount Value</label><input type="number" required min="0.01" step="0.01" value={form.discount_value} onChange={e=>setForm({...form, discount_value: e.target.value})} className={inputCls} placeholder={form.discount_type === 'percentage' ? 'e.g. 20' : 'e.g. 5.00'} data-testid="coupon-value-input"/></div>
                        <div><label className={labelCls}>Max Uses (-1 = unlimited)</label><input type="number" value={form.max_uses} onChange={e=>setForm({...form, max_uses: e.target.value})} className={inputCls} data-testid="coupon-max-uses-input"/></div>
                        <div><label className={labelCls}>Expires At (optional)</label><input type="datetime-local" value={form.expires_at} onChange={e=>setForm({...form, expires_at: e.target.value})} className={inputCls} data-testid="coupon-expires-input"/></div>
                    </div>
                    {err && <p className="text-red-600 text-sm font-body bg-red-50 border border-red-200 rounded p-3">{err}</p>}
                    <button type="submit" className="bg-navy hover:bg-navy/80 text-white font-bold px-8 py-3 rounded-sm font-body min-h-[44px]" data-testid="coupon-submit-btn">Create Coupon</button>
                </form>
            )}
            {loading ? <p className="text-navy/60 font-body">Loading...</p> : coupons.length === 0 ? (
                <p className="text-navy/60 font-body text-lg py-8 text-center">No coupons created yet.</p>
            ) : (
                <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
                    <table className="w-full font-body text-sm">
                        <thead><tr className="border-b text-navy/60 text-left bg-gray-50"><th className="p-4">Code</th><th className="p-4">Discount</th><th className="p-4">Uses</th><th className="p-4">Expires</th><th className="p-4">Actions</th></tr></thead>
                        <tbody>{coupons.map(c => (
                            <tr key={c.coupon_id} className="border-b border-gray-50" data-testid={`coupon-row-${c.coupon_id}`}>
                                <td className="p-4 font-mono font-bold text-navy">{c.code}</td>
                                <td className="p-4 text-gold font-bold">{c.discount_type === 'percentage' ? `${c.discount_value}%` : `$${c.discount_value.toFixed(2)}`}</td>
                                <td className="p-4 text-navy/70">{c.times_used} / {c.max_uses === -1 ? 'Unlimited' : c.max_uses}</td>
                                <td className="p-4 text-navy/70">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : 'Never'}</td>
                                <td className="p-4"><button onClick={() => handleDelete(c.coupon_id)} className="text-red-500 hover:text-red-700" data-testid={`delete-coupon-${c.coupon_id}`}><Trash2 size={16}/></button></td>
                            </tr>
                        ))}</tbody>
                    </table>
                </div>
            )}
        </div>
    );
}


// ─── ADMIN NEWSLETTER ─────────────────────────────────────────────
function AdminNewsletter() {
    const [subscribers, setSubscribers] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        axios.get(`${API}/admin/newsletter`, { withCredentials: true })
            .then(r => setSubscribers(r.data)).catch(() => {}).finally(() => setLoading(false));
    }, []);
    return (
        <div data-testid="admin-newsletter">
            <h2 className="font-heading text-3xl text-navy font-bold mb-8">Newsletter Subscribers</h2>
            <p className="font-body text-navy/60 mb-6">Subscribers from the homepage newsletter signup. Total: <span className="font-bold text-navy">{subscribers.length}</span></p>
            {loading ? <p className="text-navy/60 font-body">Loading...</p> : subscribers.length === 0 ? (
                <p className="text-navy/60 font-body text-lg py-8 text-center">No subscribers yet.</p>
            ) : (
                <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
                    <table className="w-full font-body text-sm">
                        <thead><tr className="border-b text-navy/60 text-left bg-gray-50"><th className="p-4">Email</th><th className="p-4">Subscribed</th></tr></thead>
                        <tbody>{subscribers.map(s => (
                            <tr key={s.subscriber_id} className="border-b border-gray-50" data-testid={`subscriber-row-${s.subscriber_id}`}>
                                <td className="p-4 text-navy font-semibold">{s.email}</td>
                                <td className="p-4 text-navy/70">{new Date(s.subscribed_at).toLocaleDateString()}</td>
                            </tr>
                        ))}</tbody>
                    </table>
                </div>
            )}
        </div>
    );
}


// ─── ADMIN DELIVERY LOG ───────────────────────────────────────────
function AdminDeliveryLog() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ channel: '', status: '', email: '' });

    const fetchLogs = () => {
        const params = new URLSearchParams();
        if (filters.channel) params.set('channel', filters.channel);
        if (filters.status) params.set('status', filters.status);
        if (filters.email) params.set('email', filters.email);
        axios.get(`${API}/admin/delivery-log?${params}`, { withCredentials: true })
            .then(r => setLogs(r.data)).catch(() => {}).finally(() => setLoading(false));
    };
    useEffect(() => { fetchLogs(); }, [filters.channel, filters.status]);

    const channels = ['platform_read', 'platform_download', 'email_attachment', 'email_link', 'sms_link', 'admin_download'];
    const statuses = ['delivered', 'failed', 'expired', 'already_used'];
    const selCls = "font-body text-sm border border-gray-200 rounded px-3 py-2 min-h-[38px] bg-white";

    return (
        <div data-testid="admin-delivery-log">
            <h2 className="font-heading text-2xl text-navy font-bold mb-6">Delivery Log</h2>
            <div className="flex flex-wrap gap-3 mb-6">
                <select value={filters.channel} onChange={e => setFilters({ ...filters, channel: e.target.value })} className={selCls} data-testid="filter-channel">
                    <option value="">All Channels</option>
                    {channels.map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
                </select>
                <select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })} className={selCls} data-testid="filter-status">
                    <option value="">All Statuses</option>
                    {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <input type="text" placeholder="Search email..." value={filters.email} onChange={e => setFilters({ ...filters, email: e.target.value })} onKeyDown={e => e.key === 'Enter' && fetchLogs()} className={`${selCls} w-48`} data-testid="filter-email"/>
                <button onClick={fetchLogs} className="bg-navy text-white font-body text-sm px-4 py-2 rounded min-h-[38px]" data-testid="filter-search-btn">Search</button>
            </div>
            {loading ? <p className="font-body text-navy/60">Loading...</p> : logs.length === 0 ? (
                <p className="font-body text-navy/40 text-center py-12">No delivery logs yet.</p>
            ) : (
                <div className="overflow-x-auto rounded border border-gray-200">
                    <table className="w-full text-sm font-body">
                        <thead className="bg-gray-50 text-navy/70 uppercase text-xs tracking-wider">
                            <tr>
                                <th className="px-4 py-3 text-left">Timestamp</th>
                                <th className="px-4 py-3 text-left">Customer</th>
                                <th className="px-4 py-3 text-left">Product</th>
                                <th className="px-4 py-3 text-left">Channel</th>
                                <th className="px-4 py-3 text-left">Status</th>
                                <th className="px-4 py-3 text-left">Detail</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {logs.map((l, i) => (
                                <tr key={i} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 text-navy/70 whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                                    <td className="px-4 py-3"><span className="font-semibold text-navy">{l.customer_name}</span><br/><span className="text-navy/50 text-xs">{l.customer_email}</span></td>
                                    <td className="px-4 py-3 text-navy">{l.product_title}</td>
                                    <td className="px-4 py-3"><span className="bg-navy/10 text-navy px-2 py-1 rounded text-xs font-semibold">{l.channel?.replace('_', ' ')}</span></td>
                                    <td className="px-4 py-3"><span className={`px-2 py-1 rounded text-xs font-semibold ${l.status === 'delivered' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{l.status}</span></td>
                                    <td className="px-4 py-3 text-navy/50 text-xs max-w-[200px] truncate">{l.detail}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}


// ─── ADMIN ABOUT PHOTOS ───────────────────────────────────────────
function AdminAboutPhotos() {
    const [photos, setPhotos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [reordering, setReordering] = useState(false);
    const [captionDrafts, setCaptionDrafts] = useState({});
    const [savingCaption, setSavingCaption] = useState(null);
    const [savingShape, setSavingShape] = useState(null);
    const fileRef = React.useRef();

    const fetchPhotos = () => {
        axios.get(`${API}/about-photos`, { withCredentials: true })
            .then(r => setPhotos(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchPhotos(); }, []);

    const handleUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        for (const file of files) {
            const fd = new FormData();
            fd.append('image', file);
            try {
                await axios.post(`${API}/admin/about-photos`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
            } catch (err) {
                alert(`Failed to upload ${file.name}`);
            }
        }
        setUploading(false);
        if (fileRef.current) fileRef.current.value = '';
        fetchPhotos();
    };

    const handleDelete = async (photoId) => {
        if (!window.confirm('Remove this photo from the gallery?')) return;
        setDeleting(photoId);
        try {
            await axios.delete(`${API}/admin/about-photos/${photoId}`, { withCredentials: true });
            setPhotos(prev => prev.filter(p => p.photo_id !== photoId));
        } catch { alert('Failed to delete photo'); }
        setDeleting(null);
    };

    const handleMove = async (index, direction) => {
        const swapIndex = direction === 'up' ? index - 1 : index + 1;
        if (swapIndex < 0 || swapIndex >= photos.length) return;
        const newPhotos = [...photos];
        [newPhotos[index], newPhotos[swapIndex]] = [newPhotos[swapIndex], newPhotos[index]];
        setPhotos(newPhotos);
        setReordering(true);
        try {
            const ordered_ids = newPhotos.map(p => p.photo_id);
            await axios.put(`${API}/admin/about-photos/reorder`, { ordered_ids }, { withCredentials: true });
        } catch { alert('Failed to save order'); fetchPhotos(); }
        setReordering(false);
    };

    const handleSaveCaption = async (photoId) => {
        const caption = captionDrafts[photoId] ?? '';
        setSavingCaption(photoId);
        try {
            await axios.patch(`${API}/admin/about-photos/${photoId}`, { caption }, { withCredentials: true });
            setPhotos(prev => prev.map(p => p.photo_id === photoId ? { ...p, caption } : p));
            setCaptionDrafts(prev => { const n = { ...prev }; delete n[photoId]; return n; });
        } catch { alert('Failed to save caption'); }
        setSavingCaption(null);
    };

    const handleShapeChange = async (photoId, shape) => {
        setSavingShape(photoId);
        // Optimistic update so Alice sees the crop change immediately.
        setPhotos(prev => prev.map(p => p.photo_id === photoId ? { ...p, display_shape: shape } : p));
        try {
            await axios.patch(`${API}/admin/about-photos/${photoId}`, { display_shape: shape }, { withCredentials: true });
            // Reload so the URL swaps to the correct derivative for the new shape.
            fetchPhotos();
        } catch (e) {
            alert('Failed to save shape. Try again.');
            fetchPhotos();
        }
        setSavingShape(null);
    };

    const SHAPE_OPTIONS = [
        { value: 'square', label: 'Square', ratio: 'aspect-square' },
        { value: 'landscape', label: 'Landscape (wide)', ratio: 'aspect-[3/2]' },
        { value: 'portrait', label: 'Portrait (tall)', ratio: 'aspect-[2/3]' },
        { value: 'original', label: 'Original (no crop)', ratio: 'aspect-auto' },
    ];

    return (
        <div data-testid="admin-about-photos">
            <div className="flex items-center justify-between mb-8">
                <h2 className="font-heading text-2xl text-navy font-bold">About Photos</h2>
                <div className="flex items-center gap-2">
                    <button type="button" onClick={async () => {
                        if (!window.confirm('Rebuild multi-size versions for every existing photo? Safe to run any time.')) return;
                        try {
                            const r = await axios.post(`${API}/admin/about-photos/regenerate-derivatives`, {}, { withCredentials: true });
                            alert(`Regenerated ${r.data.processed} photo(s). ${r.data.failed?.length || 0} failed.`);
                            fetchPhotos();
                        } catch (e) { alert('Regenerate failed: ' + (e.response?.data?.detail || e.message)); }
                    }} className="text-xs bg-ivory border border-navy/20 text-navy font-semibold px-3 py-2 rounded hover:bg-white" data-testid="regenerate-derivatives-btn">
                        Regenerate All
                    </button>
                    <label className={`flex items-center gap-2 bg-gold hover:bg-gold-hover text-navy font-bold px-5 py-2.5 rounded cursor-pointer font-body text-sm transition-colors min-h-[44px] ${uploading ? 'opacity-50 pointer-events-none' : ''}`} data-testid="upload-about-photo-btn">
                        <Plus size={18}/> {uploading ? 'Uploading...' : 'Add Photos'}
                        <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" data-testid="upload-about-photo-input"/>
                    </label>
                </div>
            </div>
            <p className="font-body text-navy/60 text-sm mb-6">These photos appear in the "Meet Alice" section on the homepage. Use the arrows to reorder, or hover to delete.</p>
            {loading ? <p className="font-body text-navy/60">Loading photos...</p> : photos.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                    <Image size={48} className="mx-auto text-navy/20 mb-4"/>
                    <p className="font-body text-navy/40">No photos yet. Upload some to get started.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {photos.map((p, idx) => {
                        const src = p.url?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${p.url}` : p.url;
                        const shape = p.display_shape || 'square';
                        const previewRatio = ({square:'aspect-square',landscape:'aspect-[3/2]',portrait:'aspect-[2/3]',original:'aspect-auto'})[shape];
                        return (
                            <div key={p.photo_id} className="relative group rounded-lg overflow-hidden border border-gray-200 shadow-sm bg-white" data-testid={`about-photo-card-${p.photo_id}`}>
                                <div className={`bg-navy/5 ${previewRatio} flex items-center justify-center overflow-hidden`} data-testid={`about-photo-preview-${p.photo_id}`}>
                                    <img src={src} alt={`Gallery photo ${idx + 1}`} className="max-w-full max-h-full w-auto h-auto object-contain"/>
                                </div>
                                {/* SHAPE PICKER — the big obvious per-photo control */}
                                <div className="border-t border-gray-200 bg-ivory/60 px-3 py-3">
                                    <label className="font-body text-navy/70 text-xs font-bold uppercase tracking-wider block mb-1.5">
                                        Photo Shape
                                    </label>
                                    <select
                                        value={shape}
                                        disabled={savingShape === p.photo_id}
                                        onChange={e => handleShapeChange(p.photo_id, e.target.value)}
                                        className="w-full bg-white border-2 border-gold/50 focus:border-gold focus:ring-2 focus:ring-gold/30 focus:outline-none rounded px-3 py-2.5 font-body text-navy text-base font-semibold cursor-pointer disabled:opacity-60 min-h-[44px]"
                                        data-testid={`photo-shape-select-${p.photo_id}`}
                                    >
                                        {SHAPE_OPTIONS.map(o => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                                    <p className="font-body text-navy/50 text-xs mt-1">
                                        {savingShape === p.photo_id ? 'Saving…' : 'Changes save automatically. The homepage updates immediately.'}
                                    </p>
                                </div>
                                {/* Reorder + Delete row */}
                                <div className="flex items-center justify-between bg-white border-t border-gray-200 px-2 py-1.5">
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => handleMove(idx, 'up')}
                                            disabled={idx === 0 || reordering}
                                            className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                            title="Move left"
                                            data-testid={`move-photo-up-${idx}`}
                                        >
                                            <ArrowUp size={16} className="text-navy"/>
                                        </button>
                                        <button
                                            onClick={() => handleMove(idx, 'down')}
                                            disabled={idx === photos.length - 1 || reordering}
                                            className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                            title="Move right"
                                            data-testid={`move-photo-down-${idx}`}
                                        >
                                            <ArrowDown size={16} className="text-navy"/>
                                        </button>
                                        <span className="text-xs text-navy/40 font-body ml-1">Photo {idx + 1}</span>
                                    </div>
                                    <button
                                        onClick={() => handleDelete(p.photo_id)}
                                        disabled={deleting === p.photo_id}
                                        className="p-1.5 rounded hover:bg-red-50 text-red-500 hover:text-red-700 transition-colors"
                                        title="Remove photo"
                                        data-testid={`delete-about-photo-${p.photo_id}`}
                                    >
                                        <Trash2 size={16}/>
                                    </button>
                                </div>
                                {/* Caption editor */}
                                <div className="border-t border-gray-100 bg-ivory/50 px-2 py-2 flex items-center gap-2">
                                    <input
                                        type="text"
                                        placeholder="Add a caption (optional)…"
                                        value={captionDrafts[p.photo_id] ?? p.caption ?? ''}
                                        onChange={e => setCaptionDrafts(prev => ({ ...prev, [p.photo_id]: e.target.value }))}
                                        className="flex-1 bg-white border border-gray-300 rounded text-navy text-xs px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-gold"
                                        data-testid={`caption-input-photo-${p.photo_id}`}
                                    />
                                    {((captionDrafts[p.photo_id] ?? null) !== null) && (captionDrafts[p.photo_id] !== (p.caption ?? '')) && (
                                        <button
                                            type="button"
                                            onClick={() => handleSaveCaption(p.photo_id)}
                                            disabled={savingCaption === p.photo_id}
                                            className="text-xs bg-gold text-navy font-bold px-2 py-1.5 rounded hover:bg-gold-hover disabled:opacity-60"
                                            data-testid={`caption-save-photo-${p.photo_id}`}
                                        >
                                            {savingCaption === p.photo_id ? '…' : 'Save'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}


// ─── ADMIN ABOUT VIDEOS (mirrors AdminAboutPhotos exactly) ────────
function AdminAboutVideos() {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [reordering, setReordering] = useState(false);
    const [captionDrafts, setCaptionDrafts] = useState({});
    const [savingCaption, setSavingCaption] = useState(null);
    const fileRef = React.useRef();

    const fetchVideos = () => {
        axios.get(`${API}/about-videos`, { withCredentials: true })
            .then(r => setVideos(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchVideos(); }, []);

    const handleUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        for (const file of files) {
            const fd = new FormData();
            fd.append('video', file);
            try {
                await axios.post(`${API}/admin/about-videos`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
            } catch (err) {
                alert(`Failed to upload ${file.name}: ${err.response?.data?.detail || err.message}`);
            }
        }
        setUploading(false);
        if (fileRef.current) fileRef.current.value = '';
        fetchVideos();
    };

    const handleDelete = async (videoId) => {
        if (!window.confirm('Remove this video from the gallery?')) return;
        setDeleting(videoId);
        try {
            await axios.delete(`${API}/admin/about-videos/${videoId}`, { withCredentials: true });
            setVideos(prev => prev.filter(v => v.video_id !== videoId));
        } catch { alert('Failed to delete video'); }
        setDeleting(null);
    };

    const handleMove = async (index, direction) => {
        const swapIndex = direction === 'up' ? index - 1 : index + 1;
        if (swapIndex < 0 || swapIndex >= videos.length) return;
        const newVideos = [...videos];
        [newVideos[index], newVideos[swapIndex]] = [newVideos[swapIndex], newVideos[index]];
        setVideos(newVideos);
        setReordering(true);
        try {
            const ordered_ids = newVideos.map(v => v.video_id);
            await axios.put(`${API}/admin/about-videos/reorder`, { ordered_ids }, { withCredentials: true });
        } catch { alert('Failed to save order'); fetchVideos(); }
        setReordering(false);
    };

    const handleSaveCaption = async (videoId) => {
        const caption = captionDrafts[videoId] ?? '';
        setSavingCaption(videoId);
        try {
            await axios.patch(`${API}/admin/about-videos/${videoId}`, { caption }, { withCredentials: true });
            setVideos(prev => prev.map(v => v.video_id === videoId ? { ...v, caption } : v));
            setCaptionDrafts(prev => { const n = { ...prev }; delete n[videoId]; return n; });
        } catch { alert('Failed to save caption'); }
        setSavingCaption(null);
    };

    return (
        <div data-testid="admin-about-videos">
            <div className="flex items-center justify-between mb-8">
                <h2 className="font-heading text-2xl text-navy font-bold">About Videos</h2>
                <label className={`flex items-center gap-2 bg-gold hover:bg-gold-hover text-navy font-bold px-5 py-2.5 rounded cursor-pointer font-body text-sm transition-colors min-h-[44px] ${uploading ? 'opacity-50 pointer-events-none' : ''}`} data-testid="upload-about-video-btn">
                    <Plus size={18}/> {uploading ? 'Uploading...' : 'Add Videos'}
                    <input ref={fileRef} type="file" accept="video/*" multiple onChange={handleUpload} className="hidden" data-testid="upload-about-video-input"/>
                </label>
            </div>
            <p className="font-body text-navy/60 text-sm mb-6">These videos appear directly below the photo gallery in the "Meet Alice" section on the homepage. Use the arrows to reorder, or click the trash icon to delete. Unlimited uploads.</p>
            {loading ? <p className="font-body text-navy/60">Loading videos...</p> : videos.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                    <Video size={48} className="mx-auto text-navy/20 mb-4"/>
                    <p className="font-body text-navy/40">No videos yet. Upload some to get started.</p>
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {videos.map((v, idx) => {
                        const src = v.url?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${v.url}` : v.url;
                        return (
                            <div key={v.video_id} className="relative group rounded-lg overflow-hidden border border-gray-200 shadow-sm" data-testid={`about-video-card-${v.video_id}`}>
                                <div className="aspect-square bg-black">
                                    <video src={src} controls preload="metadata" className="w-full h-full object-contain"/>
                                </div>
                                <div className="flex items-center justify-between bg-white border-t border-gray-200 px-2 py-1.5">
                                    <div className="flex items-center gap-1">
                                        <button onClick={() => handleMove(idx, 'up')} disabled={idx === 0 || reordering}
                                                className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" title="Move left" data-testid={`move-video-up-${idx}`}>
                                            <ArrowUp size={16} className="text-navy"/>
                                        </button>
                                        <button onClick={() => handleMove(idx, 'down')} disabled={idx === videos.length - 1 || reordering}
                                                className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" title="Move right" data-testid={`move-video-down-${idx}`}>
                                            <ArrowDown size={16} className="text-navy"/>
                                        </button>
                                        <span className="text-xs text-navy/40 font-body ml-1">{idx + 1}</span>
                                    </div>
                                    <button onClick={() => handleDelete(v.video_id)} disabled={deleting === v.video_id}
                                            className="p-1.5 rounded hover:bg-red-50 text-red-500 hover:text-red-700 transition-colors" title="Remove video" data-testid={`delete-about-video-${v.video_id}`}>
                                        <Trash2 size={16}/>
                                    </button>
                                </div>
                                {/* Caption editor */}
                                <div className="border-t border-gray-100 bg-ivory/50 px-2 py-2 flex items-center gap-2">
                                    <input
                                        type="text"
                                        placeholder="Add a caption (optional)…"
                                        value={captionDrafts[v.video_id] ?? v.caption ?? ''}
                                        onChange={e => setCaptionDrafts(prev => ({ ...prev, [v.video_id]: e.target.value }))}
                                        className="flex-1 bg-white border border-gray-300 rounded text-navy text-xs px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-gold"
                                        data-testid={`caption-input-video-${v.video_id}`}
                                    />
                                    {((captionDrafts[v.video_id] ?? null) !== null) && (captionDrafts[v.video_id] !== (v.caption ?? '')) && (
                                        <button
                                            type="button"
                                            onClick={() => handleSaveCaption(v.video_id)}
                                            disabled={savingCaption === v.video_id}
                                            className="text-xs bg-gold text-navy font-bold px-2 py-1.5 rounded hover:bg-gold-hover disabled:opacity-60"
                                            data-testid={`caption-save-video-${v.video_id}`}
                                        >
                                            {savingCaption === v.video_id ? '…' : 'Save'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}


// ─── ADMIN REVIEWS ─────────────────────────────────────────────────
function AdminReviews() {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/admin/reviews`, { withCredentials: true });
            setReviews(Array.isArray(r.data) ? r.data : []);
        } catch (_) { setReviews([]); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const toggleApprove = async (review_id, currentlyApproved) => {
        try {
            await axios.patch(`${API}/admin/reviews/${review_id}`, { approved: !currentlyApproved }, { withCredentials: true });
            setReviews(prev => prev.map(r => r.review_id === review_id ? { ...r, approved: !currentlyApproved } : r));
        } catch (err) { alert(err.response?.data?.detail || 'Update failed'); }
    };
    const remove = async (review_id) => {
        if (!window.confirm('Delete this review permanently?')) return;
        try {
            await axios.delete(`${API}/admin/reviews/${review_id}`, { withCredentials: true });
            setReviews(prev => prev.filter(r => r.review_id !== review_id));
        } catch (err) { alert(err.response?.data?.detail || 'Delete failed'); }
    };

    return (
        <div data-testid="admin-reviews">
            <h2 className="font-heading text-3xl font-bold text-navy mb-6">Reviews</h2>
            <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="font-heading text-lg font-bold text-navy">All Reviews</h3>
                    <span className="font-body text-sm text-navy/50" data-testid="review-count">
                        {reviews.length} total · {reviews.filter(r => r.approved).length} approved
                    </span>
                </div>
                {loading ? (
                    <p className="font-body text-navy/60 p-6">Loading…</p>
                ) : reviews.length === 0 ? (
                    <p className="font-body text-navy/60 p-6" data-testid="reviews-empty-msg">No reviews yet.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left font-body text-sm" data-testid="reviews-table">
                            <thead className="bg-navy/5 text-navy/70">
                                <tr>
                                    <th className="px-6 py-3">Name</th>
                                    <th className="px-6 py-3">Rating</th>
                                    <th className="px-6 py-3">Review</th>
                                    <th className="px-6 py-3">Date</th>
                                    <th className="px-6 py-3">Status</th>
                                    <th className="px-6 py-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reviews.map(r => (
                                    <tr key={r.review_id} className="border-t border-gray-100 hover:bg-ivory/40" data-testid={`review-row-${r.review_id}`}>
                                        <td className="px-6 py-3 text-navy font-semibold">{r.full_name}</td>
                                        <td className="px-6 py-3"><span className="text-gold text-base">{'★'.repeat(r.rating)}</span><span className="text-gold/30 text-base">{'★'.repeat(Math.max(0, 5-r.rating))}</span></td>
                                        <td className="px-6 py-3 text-navy/80 max-w-md">
                                            <p className="whitespace-pre-wrap">{r.review_text}</p>
                                            {(r.media_urls || []).length > 0 && (
                                                <div className="flex flex-wrap gap-2 mt-3" data-testid={`admin-review-media-${r.review_id}`}>
                                                    {r.media_urls.map((u, i) => {
                                                        const src = u.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;
                                                        const isVideo = /\.(mp4|mov|m4v)(\?|$)/i.test(u);
                                                        return isVideo ? (
                                                            <video key={i} src={src} controls preload="metadata" className="w-20 h-20 object-cover bg-black rounded border border-navy/10" data-testid={`admin-review-video-${r.review_id}-${i}`}/>
                                                        ) : (
                                                            <a key={i} href={src} target="_blank" rel="noopener noreferrer" className="block">
                                                                <img src={src} alt="" className="w-20 h-20 object-cover rounded border border-navy/10" loading="lazy" data-testid={`admin-review-image-${r.review_id}-${i}`}/>
                                                            </a>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-3 text-navy/60">{new Date(r.created_at).toLocaleString()}</td>
                                        <td className="px-6 py-3">
                                            <span className={`px-2 py-1 rounded-sm text-xs font-bold ${r.approved ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-800'}`} data-testid={`review-status-${r.review_id}`}>
                                                {r.approved ? 'Approved' : 'Pending'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-3">
                                            <div className="flex gap-2">
                                                <button onClick={() => toggleApprove(r.review_id, r.approved)} className={`text-xs font-bold px-3 py-1.5 rounded ${r.approved ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' : 'bg-green-600 text-white hover:bg-green-700'}`} data-testid={`review-toggle-${r.review_id}`}>
                                                    {r.approved ? 'Unapprove' : 'Approve'}
                                                </button>
                                                <button onClick={() => remove(r.review_id)} className="text-xs bg-red-50 text-red-600 hover:bg-red-100 font-bold px-3 py-1.5 rounded" data-testid={`review-delete-${r.review_id}`}>
                                                    Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}


// ─── ADMIN LAYOUT ─────────────────────────────────────────────────
function AdminLayout({ adminUser, activeSection, setActiveSection, onLogout }) {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const nav = [
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { id: 'analytics', label: 'Analytics', icon: BarChart3 },
        { id: 'products', label: 'Products', icon: Package },
        { id: 'orders', label: 'Orders', icon: ShoppingCart },
        { id: 'coupons', label: 'Coupons', icon: Tag },
        { id: 'customers', label: 'Customers', icon: Users },
        { id: 'newsletter', label: 'Newsletter', icon: Mail },
        { id: 'reviews', label: 'Reviews', icon: CalendarCheck },
        { id: 'delivery-log', label: 'Delivery Log', icon: FileText },
        { id: 'about-photos', label: 'About Photos', icon: Image },
        { id: 'about-videos', label: 'About Videos', icon: Video },
        { id: 'settings', label: 'Settings', icon: Settings },
    ];

    const Sidebar = () => (
        <div className="flex flex-col h-full bg-navy text-white">
            <div className="p-6 border-b border-gold/20">
                <h1 className="font-heading text-gold text-lg font-bold">KNOW YOUR STUFF</h1>
                <p className="font-body text-white/50 text-xs mt-1">Admin Panel</p>
            </div>
            {/* Back to Store link */}
            <a href="/" className="flex items-center gap-3 px-6 py-3 font-body text-base text-white/70 hover:text-gold hover:bg-gold/10 transition-colors border-b border-gold/10 min-h-[44px]" data-testid="admin-back-to-store">
                <Home size={18}/> Back to Store
            </a>
            <nav className="flex-1 py-4">
                {nav.map(item=>{
                    const Icon = item.icon;
                    return (
                        <button key={item.id} onClick={()=>{setActiveSection(item.id);setSidebarOpen(false);}} className={`w-full flex items-center gap-3 px-6 py-3 font-body text-base transition-colors min-h-[44px] ${activeSection===item.id?'bg-gold/20 text-gold border-l-4 border-gold':'text-white/70 hover:text-white hover:bg-white/5'}`} data-testid={`admin-nav-${item.id}`}>
                            <Icon size={18}/> {item.label}
                        </button>
                    );
                })}
            </nav>
            <div className="p-6 border-t border-gold/20">
                <p className="font-body text-white/50 text-xs mb-2">{adminUser.email}</p>
                <button onClick={onLogout} className="flex items-center gap-2 text-white/60 hover:text-gold font-body text-sm transition-colors" data-testid="admin-logout-btn"><LogOut size={16}/>Logout</button>
            </div>
        </div>
    );

    return (
        <div className="flex h-screen overflow-hidden bg-ivory font-body" data-testid="admin-layout">
            {/* Desktop Sidebar */}
            <div className="hidden md:flex flex-col w-64 flex-shrink-0"><Sidebar/></div>
            {/* Mobile Sidebar */}
            {sidebarOpen && (
                <div className="md:hidden fixed inset-0 z-50 flex">
                    <div className="w-72 flex flex-col"><Sidebar/></div>
                    <div className="flex-1 bg-navy/60" onClick={()=>setSidebarOpen(false)}/>
                </div>
            )}
            {/* Main Content */}
            <div className="flex-1 flex flex-col overflow-hidden">
                <div className="md:hidden flex items-center justify-between bg-navy px-4 py-3 border-b border-gold/20">
                    <button onClick={()=>setSidebarOpen(!sidebarOpen)} className="text-gold"><Menu size={24}/></button>
                    <span className="font-heading text-gold font-bold">KNOW YOUR STUFF</span>
                    <span/>
                </div>
                <main className="flex-1 overflow-y-auto p-6 md:p-10">
                    {activeSection === 'dashboard' && <AdminDashboard/>}
                    {activeSection === 'analytics' && <AdminAnalytics/>}
                    {activeSection === 'products' && <AdminProducts/>}
                    {activeSection === 'orders' && <AdminOrders/>}
                    {activeSection === 'coupons' && <AdminCoupons/>}
                    {activeSection === 'customers' && <AdminCustomers/>}
                    {activeSection === 'newsletter' && <AdminNewsletter/>}
                    {activeSection === 'rsvps' && <AdminReviews/>}
                    {activeSection === 'reviews' && <AdminReviews/>}
                    {activeSection === 'delivery-log' && <AdminDeliveryLog/>}
                    {activeSection === 'about-photos' && <AdminAboutPhotos/>}
                    {activeSection === 'about-videos' && <AdminAboutVideos/>}
                    {activeSection === 'settings' && <AdminSettings/>}
                </main>
            </div>
        </div>
    );
}

// ─── ADMIN PAGE (main export) ─────────────────────────────────────
export default function AdminPage() {
    const [adminUser, setAdminUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeSection, setActiveSection] = useState('dashboard');

    useEffect(() => {
        axios.get(`${API}/auth/me`, { withCredentials: true })
            .then(r => { if (r.data.role === 'admin') setAdminUser(r.data); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const handleLogout = async () => {
        try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch {}
        setAdminUser(null);
    };

    if (loading) return (
        <div className="min-h-screen bg-navy flex items-center justify-center">
            <p className="font-body text-gold text-lg">Loading...</p>
        </div>
    );

    if (!adminUser) return <AdminLogin onLogin={setAdminUser}/>;

    return <AdminLayout adminUser={adminUser} activeSection={activeSection} setActiveSection={setActiveSection} onLogout={handleLogout}/>;
}
